/* El ambiente de un tema, en movimiento: nieve que cae, pétalos, hojas,
 * estrellas que titilan, arañas que caminan, el simbionte que asoma, la
 * lluvia de Ciudad Gótica (UAT).
 *
 * ── POR QUÉ EXISTE ──────────────────────────────────────────────────────────
 *
 * Los copos de Nieve, los pétalos de Sakura y las hojas de Otoño eran puntos
 * y óvalos dibujados con degradados de CSS, repetidos en una grilla y quietos.
 * Igna (07/10): «es demasiado estático, parece hecho en Paint — no por feo,
 * por calidad». Lo que les faltaba no era resolución: es que la nieve CAE.
 *
 * ── QUÉ LO HACE CREÍBLE ─────────────────────────────────────────────────────
 *
 *   Profundidad   cada partícula tiene una distancia. Las de atrás son chicas,
 *                 lentas y tenues; las de adelante grandes, rápidas y fuera de
 *                 foco. Sin eso es una lluvia de confeti plana.
 *   Deriva        ninguna cae derecho: se hamacan, y una racha de viento las
 *                 corre a todas un poco a la vez.
 *   Giro          el pétalo y la hoja rotan y además se dan vuelta sobre su
 *                 eje —se los ve de canto un instante—, que es lo que hace
 *                 que se lean como algo liviano y no como una calcomanía.
 *   Desorden      nace cada una con su tamaño, su ritmo y su forma.
 *
 * ── LO QUE NO HACE ──────────────────────────────────────────────────────────
 *
 * No pasa por encima de los datos: el lienzo va DETRÁS del contenido y se ve
 * en los huecos, igual que el fondo quieto de antes. Son pocas y despacio; es
 * un fondo, no una animación. Y con «menos movimiento» pedido en el sistema
 * no arranca: queda el fondo quieto.
 *
 * Las formas se dibujan una sola vez en lienzos chicos (los sellos) y después
 * se estampan: redibujar un degradado por partícula y por cuadro es lo que
 * hace que estas cosas calienten una notebook.
 */

import planchaCopos from "../assets/ambiente/copos.webp";
import planchaHojas from "../assets/ambiente/hojas.webp";
import planchaPetalos from "../assets/ambiente/petalos.webp";
import planchaAranas from "../assets/ambiente/aranas.webp";
import planchaBilletes from "../assets/ambiente/billetes.webp";

/* LAS PIEZAS DE VERDAD. Doce copos, doce pétalos y doce hojas con calidad de
 * foto, que pasó Igna (07/10) en un collage; se recortaron una por una y van
 * en planchas de 4×3, cada pieza en un casillero de 128px.
 *
 * Las formas dibujadas por código de más abajo NO se fueron: son lo que se ve
 * el instante que tarda la plancha en llegar, y lo que queda si no llega. */
const PLANCHAS = {
  copo: planchaCopos,
  petalo: planchaPetalos,
  hoja: planchaHojas,
};
const CASILLERO = 128;

export const AMBIENTES = {
  nieve: { tipo: "copo", cantidad: 70 },
  sakura: { tipo: "petalo", cantidad: 24 },
  otono: { tipo: "hoja", cantidad: 18 },
  // Pocas: el fondo es una foto de cielo que ya trae sus estrellas.
  espacio: { tipo: "estrella", cantidad: 34 },
  // Los que no tienen foto ni nada que caiga: su ambiente es de lo que están
  // hechos. Arcade, el campo de estrellas de un fichín; Torre, el barrido de
  // un radar; Neón, la grilla que viene hacia uno.
  // Arcade eran píxeles cayendo; Igna (08/10): «¿y si representamos el
  // "invaders" de fondo?». La formación marcha, baja y vuelve a empezar.
  arcade: { tipo: "invasores", cantidad: 0 },
  // Los tres de foto que estaban quietos (Igna, 08/10: «podría tener algo de
  // movimiento»). Cada uno lo que se mueve en ese lugar: gaviotas y el brillo
  // del agua, la niebla que corre entre los árboles, la arena que vuela.
  playa: { tipo: "playa", cantidad: 0 },
  montana: { tipo: "montana", cantidad: 0 },
  desierto: { tipo: "desierto", cantidad: 0 },
  torre: { tipo: "radar", cantidad: 0 },
  neon: { tipo: "rejilla", cantidad: 0 },
  // Los de club: papelitos, como cuando sale el equipo. Pocos y despacio —es
  // un fondo, no un festejo— y cada uno con los colores de su camiseta.
  river: { tipo: "papelito", cantidad: 26, colores: ["#E2001A", "#FFFFFF", "#E2001A", "#111111"] },
  boca: { tipo: "papelito", cantidad: 26, colores: ["#F7C500", "#1C4FA8", "#F7C500", "#FFFFFF"] },
  racing: { tipo: "papelito", cantidad: 26, colores: ["#6CB4E4", "#FFFFFF", "#6CB4E4", "#0B2540"] },
  // Spider-Man: arañitas que caminan por la pantalla y alguna que baja
  // colgada de su hilo (ver «Las arañas», más abajo). Pocas, y nunca más de
  // diez aunque la pantalla sea enorme: con más deja de ser un fondo y pasa a
  // ser una plaga. Van en los colores del tema, oscuras sobre el fondo claro.
  aracnido: { tipo: "arana", cantidad: 8, maximo: 10, colores: ["#0F1A3D", "#1E3A8A", "#0F1A3D", "#8E0C22"] },
  // Venom: el simbionte. Zarcillos de goo negro que asoman por los bordes y
  // se vuelven a meter, gotas que se estiran y caen, venas blancas finas (ver
  // «El simbionte», más abajo). Detrás de todo, UNA mancha grande que pasea
  // despacio (Lautaro, 09/10; ver «La mancha»). Y desde el 13/10, trenzas:
  // dos o tres tentáculos enroscados uno en otro, que asoman por los bordes
  // (`trenzas`, nunca más de `maxTrenzas`) y que le salen a la mancha
  // (`trenzasMancha`, que es también su tope: en un celular, una); Lautaro
  // pidió «más tentáculos enredándose» (ver «Las trenzas»). Lentos igual, y
  // con tope aunque la pantalla sea enorme: diez zarcillos, cinco gotas,
  // cuatro trenzas de los bordes y dos de la mancha. (Tres de la mancha no
  // entran: a más de un radián una de otra, en los tres lados que tiene, no
  // siempre hay lugar.)
  // Las trenzas tienen además su piso, para las PC que no dan abasto (ver
  // «EL PISO DE LAS TRENZAS» en `crearAmbiente`).
  simbionte: {
    tipo: "simbionte",
    cantidad: 7,
    maximo: 10,
    gotas: 5,
    mancha: true,
    trenzas: 3,
    maxTrenzas: 4,
    trenzasMancha: 2,
  },
  // Batman (Guillermo, 13/10): lluvia de noche que cae despacio, y nada más
  // (ver «La lluvia», más abajo). Es el más sobrio de los temas: gotas finas
  // y tenues, unas cuarenta en una pantalla de oficina y nunca más de
  // cincuenta y seis.
  // Igna (26/10): «sumá los billetes cayendo en Batman», los del camión con
  // el graffiti del Joker. Pocos y despacio, entre la lluvia (ver «Los
  // billetes», más abajo).
  caballero: { tipo: "lluvia", cantidad: 40, maximo: 56, billetes: { cantidad: 9, maximo: 12 } },
  // Resident Evil (Igna, 23/10): la misma lluvia, sobre el complejo de
  // Umbrella bajo la tormenta.
  colmena: { tipo: "lluvia", cantidad: 40, maximo: 56 },
  isla: { tipo: "lluvia", cantidad: 44, maximo: 60 },
  // Navidad (Igna, 02/11): la nieve de «Nieve», menos tupida, sobre el pueblo
  // nevado.
  navidad: { tipo: "copo", cantidad: 46 },
};

/* LOS QUE TIENEN FOTO DE FONDO Y NADA QUE SE MUEVA. My Hero Academia tuvo
 * chispas que aparecían y se apagaban; Igna (09/10): «la imagen de fondo está
 * fantástica así sola, no hacen falta esos destellos». No va en `AMBIENTES`
 * —no arranca ningún lienzo—, pero sigue siendo un tema con foto, y lo que
 * depende de eso (las fichas de vidrio) lo tiene que seguir contando. */
// X-Men y Avengers, desde el 16/10, con su ilustración de fondo; Disney
// (22/10), con el castillo.
// Beatles (Lautaro, 03/11; ticket 621), con el follaje y el sendero.
export const FOTO_SIN_AMBIENTE = ["mha", "mutante", "vengadores", "magia", "ventanas", "cristal", "cuarteto"];

const COLORES = {
  petalo: ["#f7c6d4", "#f2a3b9", "#fbe3ea", "#e98aa6", "#f9d5df"],
  hoja: ["#e8a73a", "#9e3b22", "#8e1f2f", "#c7772a", "#b5541f", "#d9982c"],
  estrella: ["#ffffff", "#dfe3ff", "#aab4ff", "#fff3d6"],
  pixel: ["#ffffff", "#ffd400", "#3ba7ff", "#ff4fb8", "#8a8ab0"],
  // Las arañas sobre un fondo oscuro: las del tema no se verían.
  arana: ["#E6EBF7", "#A9BDF2", "#E6EBF7", "#FF8FA0"],
};

/* Cuánto mide, a qué velocidad cae y cuánto se ve, según el tipo. `z` va de 0
 * (lejos) a 1 (cerca). */
const FISICA = {
  // El copo de adelante es grande a propósito: es un cristal con dibujo, y a
  // 13px no se le ve nada.
  copo: { tam: [2.5, 21], caida: [16, 62], vaiven: 18, alfa: [0.5, 0.95] },
  petalo: { tam: [9, 22], caida: [20, 52], vaiven: 34, alfa: [0.5, 0.92] },
  hoja: { tam: [13, 30], caida: [26, 70], vaiven: 46, alfa: [0.5, 0.9] },
  estrella: { tam: [0.7, 2.4], caida: [0, 0], vaiven: 0, alfa: [0.35, 1] },
  // Cuadrados que bajan derecho y a tres velocidades: sin vaivén ni viento,
  // que en un fichín no hay.
  pixel: { tam: [1, 3], caida: [7, 44], vaiven: 0, alfa: [0.3, 0.95] },
  // El papelito cae lento y se hamaca mucho: es papel, no nieve.
  papelito: { tam: [3, 7], caida: [18, 46], vaiven: 38, alfa: [0.55, 0.95] },
  // El billete es papel grande: cae como la hoja, un poco más lento, y el de
  // atrás apenas se ve (es un fondo de trabajo, no una lluvia de plata).
  billete: { tam: [18, 40], caida: [22, 58], vaiven: 42, alfa: [0.45, 0.9] },
  radar: { tam: [0, 0], caida: [0, 0], vaiven: 0, alfa: [0, 0] },
  rejilla: { tam: [0, 0], caida: [0, 0], vaiven: 0, alfa: [0, 0] },
};

const entre = ([a, b], t) => a + (b - a) * t;

/* Una partícula nueva. `azar` se pasa (y no se llama a Math.random acá) para
 * poder probar esto con una secuencia fija. `arriba` la hace nacer por encima
 * de la pantalla, que es como entran las que reemplazan a las que salieron;
 * las del arranque nacen repartidas, o la pantalla empieza vacía. */
export function nuevaParticula(tipo, ancho, alto, azar = Math.random, arriba = false) {
  const f = FISICA[tipo];
  // Más de las de atrás que de las de adelante, como en una nevada de verdad.
  const z = Math.pow(azar(), 1.7);
  const tam = entre(f.tam, z);
  return {
    tipo,
    z,
    tam,
    x: azar() * ancho,
    y: arriba ? -tam - azar() * alto * 0.25 : azar() * alto,
    caida: entre(f.caida, z) * (0.8 + azar() * 0.4),
    vaiven: f.vaiven * (0.5 + z * 0.7),
    ritmo: 0.35 + azar() * 0.75,
    fase: azar() * Math.PI * 2,
    giro: azar() * Math.PI * 2,
    // El copo gira apenas —es un cristal, no una hoja— y no se da vuelta.
    vgiro: (azar() - 0.5) * (tipo === "copo" ? 0.5 : 1.6),
    vuelta: azar() * Math.PI * 2,
    vvuelta: tipo === "copo" ? 0 : 0.6 + azar() * 1.6,
    alfa: entre(f.alfa, z),
    sello: Math.floor(azar() * 1e6),
  };
}

/* La racha: un viento lento que las corre a todas hacia el mismo lado, a
 * veces, y nunca del todo quieto. */
export const racha = (t) =>
  Math.sin(t * 0.13) * 9 + Math.sin(t * 0.31 + 1.7) * 5;

/* Un paso de tiempo. Devuelve `true` si la partícula salió de la pantalla y
 * hay que reemplazarla. `dt` en segundos, `t` es el reloj total. */
export function avanzar(p, dt, ancho, alto, t) {
  if (p.tipo === "estrella") return false;
  p.y += p.caida * dt;
  // El viento sólo corre a lo que se hamaca: un píxel de fichín baja derecho.
  if (p.vaiven)
    p.x +=
      (Math.cos(t * p.ritmo + p.fase) * p.vaiven + racha(t) * (0.4 + p.z)) * dt;
  p.giro += p.vgiro * dt;
  p.vuelta += p.vvuelta * dt;
  // Las que el viento saca por un costado vuelven por el otro: si no, con
  // una racha larga un lado de la pantalla se queda sin nieve.
  if (p.x < -p.tam * 2) p.x = ancho + p.tam;
  else if (p.x > ancho + p.tam * 2) p.x = -p.tam;
  return p.y > alto + p.tam * 2;
}

/* ── Los sellos ────────────────────────────────────────────────────────────── */

function lienzo(lado) {
  const c = document.createElement("canvas");
  c.width = lado;
  c.height = lado;
  return c;
}

function selloCopo(lado, desenfoque, sobreClaro) {
  const c = lienzo(lado);
  const g = c.getContext("2d");
  const r = lado / 2;
  const disco = (cx, cy, radio, color, borde) => {
    const d = g.createRadialGradient(cx, cy, 0, cx, cy, radio);
    d.addColorStop(0, color);
    d.addColorStop(borde, color);
    d.addColorStop(1, color.replace(/[\d.]+\)$/, "0)"));
    g.fillStyle = d;
    g.beginPath();
    g.arc(cx, cy, radio, 0, Math.PI * 2);
    g.fill();
  };
  // Sobre un fondo claro la nieve blanca no se ve. No se le pone un filo —que
  // la vuelve una burbuja—: se le pone SOMBRA por debajo, como a cualquier
  // cosa blanca iluminada desde arriba. El copo se lee por su parte oscura.
  if (sobreClaro)
    disco(r, r * 1.14, r * 0.78, `rgba(118,152,196,${desenfoque ? 0.2 : 0.34})`, desenfoque ? 0.15 : 0.4);
  disco(r, r * 0.92, r * 0.7, `rgba(255,255,255,${desenfoque ? 0.8 : 1})`, desenfoque ? 0.2 : 0.55);
  return c;
}

function selloPetalo(lado, color, desenfoque) {
  const c = lienzo(lado);
  const g = c.getContext("2d");
  if (desenfoque && "filter" in g) g.filter = `blur(${lado * 0.045}px)`;
  const m = lado * 0.14;
  g.translate(lado / 2, lado / 2);
  const a = lado / 2 - m;
  // Un pétalo de cerezo: angosto en la base, ancho arriba, con la muesca.
  g.beginPath();
  g.moveTo(0, a);
  g.bezierCurveTo(a * 0.95, a * 0.45, a * 0.85, -a * 0.75, a * 0.22, -a * 0.92);
  g.quadraticCurveTo(0, -a * 0.62, -a * 0.22, -a * 0.92);
  g.bezierCurveTo(-a * 0.85, -a * 0.75, -a * 0.95, a * 0.45, 0, a);
  const d = g.createLinearGradient(0, a, 0, -a);
  d.addColorStop(0, "#fff7f9");
  d.addColorStop(0.55, color);
  d.addColorStop(1, color);
  g.fillStyle = d;
  g.fill();
  // La nervadura apenas insinuada.
  g.strokeStyle = "rgba(200,90,125,0.22)";
  g.lineWidth = Math.max(0.6, lado * 0.012);
  g.beginPath();
  g.moveTo(0, a * 0.85);
  g.quadraticCurveTo(a * 0.08, 0, 0, -a * 0.6);
  g.stroke();
  return c;
}

function selloHoja(lado, color, desenfoque) {
  const c = lienzo(lado);
  const g = c.getContext("2d");
  if (desenfoque && "filter" in g) g.filter = `blur(${lado * 0.04}px)`;
  const m = lado * 0.12;
  g.translate(lado / 2, lado / 2);
  const a = lado / 2 - m;
  // Una hoja simple, con punta y un borde apenas irregular.
  g.beginPath();
  g.moveTo(0, -a);
  g.bezierCurveTo(a * 0.75, -a * 0.55, a * 0.72, a * 0.25, a * 0.12, a * 0.78);
  g.lineTo(0, a * 0.7);
  g.lineTo(-a * 0.12, a * 0.78);
  g.bezierCurveTo(-a * 0.72, a * 0.25, -a * 0.75, -a * 0.55, 0, -a);
  const d = g.createLinearGradient(-a, -a, a, a);
  d.addColorStop(0, color);
  d.addColorStop(1, "rgba(60,25,10,0.55)");
  g.fillStyle = color;
  g.fill();
  g.globalAlpha = 0.35;
  g.fillStyle = d;
  g.fill();
  g.globalAlpha = 1;
  // El nervio central, las nervaduras y el cabito.
  g.strokeStyle = "rgba(60,25,10,0.4)";
  g.lineWidth = Math.max(0.7, lado * 0.016);
  g.beginPath();
  g.moveTo(0, -a * 0.9);
  g.lineTo(0, a);
  for (const y of [-0.45, -0.1, 0.25]) {
    g.moveTo(0, a * y);
    g.lineTo(a * 0.42, a * (y - 0.22));
    g.moveTo(0, a * y);
    g.lineTo(-a * 0.42, a * (y - 0.22));
  }
  g.stroke();
  return c;
}

/* Los sellos de un tipo: unos pocos, nítidos y fuera de foco, en cada color.
 * Cada partícula elige el suyo al nacer y no cambia más. */
function sellos(tipo, sobreClaro) {
  const lado = 96;
  if (tipo === "copo") {
    return {
      nitidos: [selloCopo(lado, false, sobreClaro)],
      borrosos: [selloCopo(lado, true, sobreClaro)],
    };
  }
  const hacer = tipo === "petalo" ? selloPetalo : selloHoja;
  return {
    nitidos: COLORES[tipo].map((c) => hacer(lado, c, false)),
    borrosos: COLORES[tipo].map((c) => hacer(lado, c, true)),
  };
}

/* Los sellos sacados de una plancha de fotos: cada casillero, nítido y fuera
 * de foco. Donde el navegador no sabe desenfocar un lienzo, el borroso es el
 * mismo nítido: se pierde la profundidad de campo, no la partícula. */
function sellosDeFoto(img) {
  const nitidos = [];
  const borrosos = [];
  const columnas = Math.round(img.width / CASILLERO);
  const filas = Math.round(img.height / CASILLERO);
  for (let i = 0; i < columnas * filas; i++) {
    const sx = (i % columnas) * CASILLERO;
    const sy = Math.floor(i / columnas) * CASILLERO;
    for (const borroso of [false, true]) {
      const c = lienzo(CASILLERO);
      const g = c.getContext("2d");
      if (borroso && "filter" in g) g.filter = "blur(4px)";
      g.drawImage(img, sx, sy, CASILLERO, CASILLERO, 0, 0, CASILLERO, CASILLERO);
      (borroso ? borrosos : nitidos).push(c);
    }
  }
  return { nitidos, borrosos };
}

/* ── Los ambientes que no son partículas ───────────────────────────────────── */

/* Torre de control: un radar. Anillos, la cruz, un barrido que gira despacio
 * y unos ecos que se encienden cuando el barrido les pasa por encima y se
 * van apagando. Todo muy tenue: es el fondo de una consola, no un juego. */
const ECOS = [
  [0.31, 0.42], [0.58, 0.77], [0.72, 0.23], [0.44, 0.9], [0.86, 0.55],
  [0.19, 0.68], [0.64, 0.5],
];
export function dibujarRadar(g, t, ancho, alto) {
  const cx = ancho * 0.8;
  const cy = alto * 0.74;
  const R = Math.max(ancho, alto) * 0.62;
  const verde = "61,220,151";
  g.lineWidth = 1;
  g.strokeStyle = `rgba(${verde},0.07)`;
  for (let i = 1; i <= 6; i++) {
    g.beginPath();
    g.arc(cx, cy, (R * i) / 6, 0, Math.PI * 2);
    g.stroke();
  }
  g.beginPath();
  g.moveTo(cx - R, cy);
  g.lineTo(cx + R, cy);
  g.moveTo(cx, cy - R);
  g.lineTo(cx, cy + R);
  g.stroke();

  const angulo = (t * 0.32) % (Math.PI * 2);
  if (g.createConicGradient) {
    // El barrido: una cuña que se desvanece hacia atrás.
    const cono = g.createConicGradient(angulo - 0.9, cx, cy);
    cono.addColorStop(0, `rgba(${verde},0)`);
    cono.addColorStop(0.143, `rgba(${verde},0.13)`);
    cono.addColorStop(0.1435, `rgba(${verde},0)`);
    cono.addColorStop(1, `rgba(${verde},0)`);
    g.fillStyle = cono;
    g.beginPath();
    g.arc(cx, cy, R, 0, Math.PI * 2);
    g.fill();
  }
  g.strokeStyle = `rgba(${verde},0.32)`;
  g.beginPath();
  g.moveTo(cx, cy);
  g.lineTo(cx + Math.cos(angulo) * R, cy + Math.sin(angulo) * R);
  g.stroke();

  for (const [u, v] of ECOS) {
    const a = u * Math.PI * 2;
    const d = R * (0.18 + v * 0.74);
    // Cuánto hace que el barrido pasó por este eco, en vueltas.
    let hace = (angulo - a) / (Math.PI * 2);
    hace -= Math.floor(hace);
    const brillo = Math.max(0, 1 - hace * 2.2);
    if (brillo <= 0) continue;
    const x = cx + Math.cos(a) * d;
    const y = cy + Math.sin(a) * d;
    g.fillStyle = `rgba(${verde},${0.75 * brillo})`;
    g.beginPath();
    g.arc(x, y, 2.2, 0, Math.PI * 2);
    g.fill();
    g.fillStyle = `rgba(${verde},${0.12 * brillo})`;
    g.beginPath();
    g.arc(x, y, 9, 0, Math.PI * 2);
    g.fill();
  }
}

/* Neón: el piso de grilla que viene hacia uno, con el horizonte encendido.
 * Las líneas horizontales no avanzan parejo: se separan al acercarse, que es
 * lo único que hace falta para que se lea como profundidad. */
export function dibujarRejilla(g, t, ancho, alto) {
  const horizonte = alto * 0.6;
  const fondo = alto - horizonte;
  const cx = ancho / 2;

  const brillo = g.createLinearGradient(0, horizonte - 90, 0, horizonte + 26);
  brillo.addColorStop(0, "rgba(43,217,255,0)");
  brillo.addColorStop(0.82, "rgba(255,43,214,0.16)");
  brillo.addColorStop(1, "rgba(255,43,214,0)");
  g.fillStyle = brillo;
  g.fillRect(0, horizonte - 90, ancho, 116);

  g.lineWidth = 1;
  const paso = (t * 0.22) % 1;
  const N = 13;
  for (let n = 0; n < N; n++) {
    const k = (n + paso) / N;
    const y = horizonte + fondo * k * k;
    g.strokeStyle = `rgba(255,43,214,${0.05 + 0.3 * k})`;
    g.beginPath();
    g.moveTo(0, y);
    g.lineTo(ancho, y);
    g.stroke();
  }
  // Las que fugan al centro.
  const abertura = ancho * 1.5;
  for (let i = -16; i <= 16; i++) {
    const lejos = g.createLinearGradient(0, horizonte, 0, alto);
    lejos.addColorStop(0, "rgba(43,217,255,0.03)");
    lejos.addColorStop(1, "rgba(43,217,255,0.26)");
    g.strokeStyle = lejos;
    g.beginPath();
    g.moveTo(cx + i * 6, horizonte);
    g.lineTo(cx + (i / 16) * abertura, alto);
    g.stroke();
  }
  g.strokeStyle = "rgba(43,217,255,0.5)";
  g.beginPath();
  g.moveTo(0, horizonte);
  g.lineTo(ancho, horizonte);
  g.stroke();
}

/* ── Las escenas de los temas de foto ────────────────────────────────────────
 * Ninguna guarda estado: dónde está cada cosa sale del reloj, así que se
 * pueden probar pidiéndoles un instante. `suerte(i)` es un número fijo entre
 * 0 y 1 para cada índice — el desorden de siempre, sin Math.random. */
export const suerte = (i) => {
  const x = Math.sin(i * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const vuelta = (v, largo) => ((v % largo) + largo) % largo;

/* Un pájaro lejano: dos alas que suben y bajan. */
function pajaro(g, x, y, tam, aleteo) {
  const punta = tam * (0.25 + 0.55 * aleteo);
  g.beginPath();
  g.moveTo(x - tam, y - punta);
  g.quadraticCurveTo(x - tam * 0.45, y - tam * 0.5, x, y);
  g.quadraticCurveTo(x + tam * 0.45, y - tam * 0.5, x + tam, y - punta);
  g.stroke();
}

/* Playa: gaviotas que cruzan planeando y el sol que pica en el agua. */
export function dibujarPlaya(g, t, ancho, alto) {
  g.lineCap = "round";
  for (let i = 0; i < 4; i++) {
    const lejos = 0.45 + suerte(i) * 0.55;
    const x = vuelta(t * (14 + 16 * lejos) + suerte(i + 9) * (ancho + 240), ancho + 240) - 120;
    const y = alto * (0.12 + suerte(i + 4) * 0.3) + Math.sin(t * 0.5 + i * 2) * 9;
    // Planea un rato y aletea otro.
    const aleteo = Math.max(0, Math.sin(t * 0.35 + i * 1.9)) * (0.5 + 0.5 * Math.sin(t * 7 + i));
    g.strokeStyle = `rgba(38,58,72,${0.2 + 0.25 * lejos})`;
    g.lineWidth = 1 + lejos;
    pajaro(g, x, y, 7 + 9 * lejos, aleteo);
  }
  // Los destellos: cruces chicas que se prenden y se apagan, abajo.
  g.strokeStyle = "#ffffff";
  for (let i = 0; i < 18; i++) {
    const brillo = Math.sin(t * (0.9 + suerte(i + 20) * 1.6) + suerte(i + 40) * 6.28);
    if (brillo < 0.35) continue;
    const x = suerte(i + 60) * ancho;
    const y = alto * (0.7 + suerte(i + 80) * 0.26);
    const r = (2 + suerte(i + 100) * 4) * brillo;
    g.globalAlpha = (brillo - 0.35) * 1.2;
    g.lineWidth = 1.2;
    g.beginPath();
    g.moveTo(x - r, y);
    g.lineTo(x + r, y);
    g.moveTo(x, y - r);
    g.lineTo(x, y + r);
    g.stroke();
  }
  g.globalAlpha = 1;
}

/* Montaña: bancos de niebla que corren despacio entre los árboles, y un par
 * de aves altas. */
export function dibujarMontana(g, t, ancho, alto) {
  for (let i = 0; i < 6; i++) {
    const radio = ancho * (0.2 + suerte(i) * 0.2);
    const tramo = ancho + radio * 2;
    const x = vuelta(t * (5 + suerte(i + 5) * 9) + suerte(i + 10) * tramo, tramo) - radio;
    const y = alto * (0.5 + suerte(i + 15) * 0.42) + Math.sin(t * 0.12 + i) * 10;
    const banco = g.createRadialGradient(x, y, 0, x, y, radio);
    const fuerza = 0.16 + suerte(i + 20) * 0.14;
    banco.addColorStop(0, `rgba(255,255,255,${fuerza})`);
    banco.addColorStop(1, "rgba(255,255,255,0)");
    g.fillStyle = banco;
    // Achatado: un banco de niebla es ancho y bajo.
    g.save();
    g.translate(0, y * 0.62);
    g.scale(1, 0.38);
    g.beginPath();
    g.arc(x, y, radio, 0, Math.PI * 2);
    g.fill();
    g.restore();
  }
  g.lineCap = "round";
  g.strokeStyle = "rgba(34,48,40,0.38)";
  g.lineWidth = 1.4;
  for (let i = 0; i < 2; i++) {
    const x = vuelta(t * (9 + i * 4) + suerte(i + 30) * (ancho + 200), ancho + 200) - 100;
    const y = alto * (0.14 + i * 0.09) + Math.sin(t * 0.3 + i * 3) * 14;
    pajaro(g, x, y, 9 - i * 2, 0.5 + 0.5 * Math.sin(t * 1.6 + i));
  }
}

/* Desierto: la arena que el viento levanta a ras de las dunas, en rachas, y
 * cada tanto una mata rodando. */
export function dibujarDesierto(g, t, ancho, alto) {
  const viento = 0.55 + 0.45 * Math.sin(t * 0.21) * Math.sin(t * 0.07 + 1);
  g.lineCap = "round";
  for (let i = 0; i < 70; i++) {
    const cerca = suerte(i);
    const tramo = ancho + 160;
    const x = vuelta(t * (60 + 150 * cerca) + Math.sin(t * 0.21) * 90 + suerte(i + 200) * tramo, tramo) - 80;
    const y = alto * (0.52 + suerte(i + 400) * 0.46) + Math.sin(t * 1.3 + i) * 5;
    const largo = (8 + 26 * cerca) * (0.4 + viento);
    g.strokeStyle = `rgba(178,112,52,${(0.1 + 0.22 * cerca) * (0.5 + viento * 0.5)})`;
    g.lineWidth = 0.8 + cerca * 1.1;
    g.beginPath();
    g.moveTo(x, y);
    g.lineTo(x - largo, y + largo * 0.07);
    g.stroke();
  }
  // La mata: pasa una vez cada tanto, rodando y a los saltitos.
  const ciclo = vuelta(t, 34);
  if (ciclo < 14) {
    const k = ciclo / 14;
    const x = -40 + k * (ancho + 80);
    const r = 15;
    const y = alto * 0.9 - Math.abs(Math.sin(k * 26)) * 16;
    g.strokeStyle = "rgba(110,72,36,0.5)";
    g.lineWidth = 1;
    for (let j = 0; j < 7; j++) {
      const giro = k * 40 + j * 0.9;
      g.beginPath();
      g.arc(x + Math.cos(giro) * 3, y + Math.sin(giro) * 3, r - (j % 3) * 3, giro, giro + 4.2);
      g.stroke();
    }
  }
}

/* Arcade: la formación de invasores. Cada dibujo es una grilla de unos y
 * ceros, en dos poses; marchan de a pasos —no deslizándose—, bajan al tocar
 * el borde y, cuando llegaron abajo, la partida empieza de nuevo. */
const INVASORES = [
  [
    ["00011000", "00111100", "01111110", "11011011", "11111111", "00100100", "01011010", "10100101"],
    ["00011000", "00111100", "01111110", "11011011", "11111111", "01011010", "10000001", "01000010"],
  ],
  [
    ["00100000100", "00010001000", "00111111100", "01101110110", "11111111111", "10111111101", "10100000101", "00011011000"],
    ["00100000100", "10010001001", "10111111101", "11101110111", "11111111111", "01111111110", "00100000100", "01000000010"],
  ],
  [
    ["000011110000", "011111111110", "111111111111", "111001100111", "111111111111", "000110011000", "001101101100", "110000000011"],
    ["000011110000", "011111111110", "111111111111", "111001100111", "111111111111", "001110011100", "011001100110", "001100001100"],
  ],
];
const FILAS_INVASORES = [0, 1, 1, 2, 2];
const COLOR_INVASOR = ["#ff4fb8", "#3ba7ff", "#3ba7ff", "#ffd400", "#ffd400"];

function estampar(g, dibujo, x, y, px) {
  for (let f = 0; f < dibujo.length; f++) {
    const fila = dibujo[f];
    for (let c = 0; c < fila.length; c++) {
      if (fila[c] !== "1") continue;
      // Corridas de unos seguidos van en un solo rectángulo.
      let fin = c;
      while (fila[fin + 1] === "1") fin++;
      g.fillRect(x + c * px, y + f * px, (fin - c + 1) * px, px);
      c = fin;
    }
  }
}

/* Dónde está la formación en el paso `n`: va y viene, y baja un renglón en
 * cada borde. Se exporta para poder probar la marcha sin dibujar. */
export function marchaInvasores(n, pasosPorLado, bajadas) {
  const vueltaEntera = (pasosPorLado + 1) * bajadas;
  const m = vuelta(n, vueltaEntera);
  const renglon = Math.floor(m / (pasosPorLado + 1));
  const enLado = Math.min(m % (pasosPorLado + 1), pasosPorLado);
  return { renglon, paso: renglon % 2 ? pasosPorLado - enLado : enLado };
}

/* LA PARTIDA: lo único de este archivo que guarda estado entre cuadros.
 *
 * Igna (08/10): «lo único que me hace mal es que los proyectiles no le hacen
 * nada a los aliens». El disparo era un adorno que subía y pasaba de largo.
 * Ahora pega: el invasor explota y no vuelve hasta la próxima oleada, y el
 * cañón apunta —se corre hasta quedar abajo de uno vivo antes de tirar—, que
 * si no erraría casi siempre por los huecos de la formación. */
export const nuevaPartida = () => ({
  t: null,
  muertos: new Set(),
  tiro: null,
  recarga: 0,
  explosiones: [],
  canon: null,
  oleada: -1,
  descanso: 0,
});

const EXPLOSION = ["10010001", "01001010", "00000000", "11000011", "00000000", "01010010", "10001001", "00000000"];

export function dibujarInvasores(g, t, ancho, alto, partida = nuevaPartida()) {
  const px = ancho < 700 ? 2 : 3;
  const columnas = Math.max(5, Math.min(11, Math.floor((ancho * 0.6) / (16 * px))));
  const filas = FILAS_INVASORES.length;
  const celda = 16 * px;
  const anchoFormacion = columnas * celda;
  const libre = Math.max(0, ancho - anchoFormacion - 40);
  const pasosPorLado = Math.max(1, Math.floor(libre / (4 * px)));
  const n = Math.floor(t / 0.55);
  const { renglon, paso } = marchaInvasores(n, pasosPorLado, 9);
  const x0 = 20 + paso * 4 * px;
  const y0 = alto * 0.14 + renglon * 6 * px;
  const pose = n % 2;
  const dt = partida.t === null ? 0 : Math.min(0.05, Math.max(0, t - partida.t));
  partida.t = t;

  // Oleada nueva: cuando la marcha vuelve a empezar, o un rato después de
  // que cayó el último.
  const oleada = Math.floor(n / ((pasosPorLado + 1) * 9));
  if (oleada !== partida.oleada) {
    partida.oleada = oleada;
    partida.muertos.clear();
  }
  if (partida.muertos.size >= filas * columnas) {
    partida.descanso += dt;
    if (partida.descanso > 1.6) {
      partida.muertos.clear();
      partida.descanso = 0;
    }
  }

  const lugar = (f, c) => {
    const dibujo = INVASORES[FILAS_INVASORES[f]][pose];
    const centrar = Math.round(((12 - dibujo[0].length) / 2) * px);
    return {
      dibujo,
      x: Math.round(x0 + c * celda + centrar),
      y: Math.round(y0 + f * 12 * px),
      w: dibujo[0].length * px,
      h: dibujo.length * px,
    };
  };

  g.globalAlpha = 0.3;
  for (let f = 0; f < filas; f++) {
    g.fillStyle = COLOR_INVASOR[f];
    for (let c = 0; c < columnas; c++) {
      if (partida.muertos.has(f * 100 + c)) continue;
      const l = lugar(f, c);
      estampar(g, l.dibujo, l.x, l.y, px);
    }
  }

  // El cañón busca la columna con vivos más cercana y se le pone abajo.
  const cy = Math.round(alto - 22 * px);
  if (partida.canon === null) partida.canon = ancho / 2;
  let blanco = null;
  for (let c = 0; c < columnas; c++) {
    let vivo = -1;
    for (let f = filas - 1; f >= 0 && vivo < 0; f--) if (!partida.muertos.has(f * 100 + c)) vivo = f;
    if (vivo < 0) continue;
    const l = lugar(vivo, c);
    const centro = l.x + l.w / 2;
    if (blanco === null || Math.abs(centro - partida.canon) < Math.abs(blanco - partida.canon)) blanco = centro;
  }
  if (blanco !== null) {
    const falta = blanco - partida.canon;
    partida.canon += Math.sign(falta) * Math.min(Math.abs(falta), 150 * dt);
  }
  partida.canon = Math.min(ancho - 8 * px, Math.max(7 * px, partida.canon));
  const cx = Math.round(partida.canon);
  g.fillStyle = "#3dffb0";
  g.fillRect(cx - 6 * px, cy, 13 * px, 4 * px);
  g.fillRect(cx - px, cy - 3 * px, 3 * px, 3 * px);

  // El disparo: sale cuando el cañón está en posición, sube, y si toca a uno
  // vivo lo saca.
  partida.recarga -= dt;
  if (!partida.tiro && partida.recarga <= 0 && blanco !== null && Math.abs(blanco - partida.canon) < 2 * px)
    partida.tiro = { x: cx, y: cy - 7 * px };
  if (partida.tiro) {
    const tiro = partida.tiro;
    tiro.y -= alto * 0.85 * dt;
    let pego = false;
    for (let f = filas - 1; f >= 0 && !pego; f--) {
      for (let c = 0; c < columnas && !pego; c++) {
        if (partida.muertos.has(f * 100 + c)) continue;
        const l = lugar(f, c);
        if (tiro.x >= l.x && tiro.x <= l.x + l.w && tiro.y <= l.y + l.h && tiro.y + 4 * px >= l.y) {
          partida.muertos.add(f * 100 + c);
          partida.explosiones.push({ x: l.x, y: l.y, vida: 0 });
          pego = true;
        }
      }
    }
    if (pego || tiro.y < 0) {
      partida.tiro = null;
      partida.recarga = 0.45;
    } else {
      g.fillStyle = "#ffffff";
      g.globalAlpha = 0.7;
      g.fillRect(tiro.x, Math.round(tiro.y), px, 4 * px);
    }
  }
  // La explosión dura un instante, donde estaba el invasor.
  g.fillStyle = "#ffffff";
  partida.explosiones = partida.explosiones.filter((e) => (e.vida += dt) < 0.28);
  for (const e of partida.explosiones) {
    g.globalAlpha = 0.6 * (1 - e.vida / 0.28);
    estampar(g, EXPLOSION, e.x, e.y, px);
  }
  // Unas pocas estrellas fijas que parpadean, clavadas a la grilla.
  for (let i = 0; i < 26; i++) {
    g.globalAlpha = 0.18 + 0.3 * (0.5 + 0.5 * Math.sin(t * (1 + suerte(i) * 3) + i));
    g.fillRect(Math.round(suerte(i + 50) * ancho), Math.round(suerte(i + 90) * alto), px - 1 || 1, px - 1 || 1);
  }
  g.globalAlpha = 1;
}

/* ── Las arañas ──────────────────────────────────────────────────────────────
 *
 * Lautaro (Satelital, 08/10), por mail a Cepi: «Quiero uno (si se puede) que
 * sea sobre spider-man. Serían los colores rojo y azul con arañas caminando
 * por la pantalla o algo así».
 *
 * La regla es la de los clubes: van los colores, no el escudo. Acá no hay
 * máscara, ni la araña del pecho del personaje, ni ninguna imagen suya: son
 * arañitas cualesquiera, dibujadas acá con dos óvalos y ocho patas.
 *
 * ── QUÉ LAS HACE CREÍBLES ───────────────────────────────────────────────────
 *
 *   Las patas     cada una tiene rodilla, y se mueven de a cuatro: la 1 y la
 *                 3 de un lado con la 2 y la 4 del otro, y después al revés,
 *                 como caminan las de verdad. Y se mueven según lo que la
 *                 araña AVANZÓ, no según el reloj: parada, no las mueve; si
 *                 fueran con el reloj patinaría.
 *   A los tirones camina un rato y se queda quieta otro. Una araña que anda
 *                 siempre a la misma velocidad parece un juguete a cuerda.
 *   El rumbo      dobla de a poco, con dos senos lentos: trayectorias suaves,
 *                 nunca un zigzag.
 *   El hilo       alguna baja colgada desde arriba, se queda un rato
 *                 hamacándose y vuelve a subir trepando (ahí sí mueve las
 *                 patas).
 *
 * Una araña es un trazo para las ocho patas (dos si cuelga: el hilo) y uno o
 * dos rellenos para el cuerpo, por cuadro: diez arañas, nada. Guardan
 * estado entre cuadros, como la partida de Arcade, pero el desorden sale de
 * `azar` al nacer y de `suerte` después — se pueden probar con una secuencia
 * fija. */

/* Las cuatro patas de un lado: hacia dónde va el pie y hacia dónde la
   rodilla (radianes desde el frente), cuánto llegan (por el largo de la
   araña) y en qué tanda pisan. La rodilla queda más hacia el costado que el
   pie —la de adelante se estira hacia adelante con el codo afuera, la de atrás
   hacia atrás—, que es la silueta de una araña vista desde arriba. Las de
   adelante son las más largas, como en casi todas. */
const PATAS = [
  { angulo: 0.55, rodilla: 1.0, largo: 1, tanda: 0 },
  { angulo: 1.2, rodilla: 1.3, largo: 0.86, tanda: 1 },
  { angulo: 1.95, rodilla: 1.85, largo: 0.84, tanda: 0 },
  { angulo: 2.55, rodilla: 2.15, largo: 0.98, tanda: 1 },
];
// Cuánto se va y vuelve cada pata al dar un paso, en radianes.
const BRAZADA = 0.17;

/* Una araña nueva. `entra` la hace aparecer desde afuera —la que reemplaza a
   una que se fue—; las del arranque nacen ya adentro, o la pantalla empieza
   vacía. Tres de cada diez son de las que bajan colgadas. */
export function nuevaArana(ancho, alto, azar = Math.random, entra = false) {
  const z = azar();
  const tam = entre([12, 22], z);
  const a = {
    z,
    tam,
    alfa: entre([0.55, 0.85], z),
    color: Math.floor(azar() * 1e6),
    semilla: Math.floor(azar() * 1e4),
    tramos: 0,
    paso: azar() * Math.PI * 2,
    vel: 0,
    velMax: entre([16, 34], z) * (0.8 + azar() * 0.4),
    // Las ganas de doblar: dos ritmos y dos fases, fijos para cada una.
    curva: [0.2 + azar() * 0.4, 0.5 + azar() * 0.8, azar() * Math.PI * 2, azar() * Math.PI * 2],
    quieta: 0,
    andando: 1 + azar() * 4,
    colgada: azar() < 0.3,
    x: azar() * ancho,
    y: azar() * alto,
    rumbo: azar() * Math.PI * 2,
  };
  if (a.colgada) {
    a.hiloX = 24 + (a.x / Math.max(1, ancho)) * Math.max(0, ancho - 48);
    a.x = a.hiloX;
    a.fondo = alto * (0.2 + azar() * 0.42);
    a.y = entra ? -a.tam * 2 : (a.y / Math.max(1, alto)) * a.fondo;
    a.fase = "baja";
    a.espera = 2 + azar() * 4;
    a.rumbo = Math.PI / 2;
    return a;
  }
  if (entra) {
    // Por uno de los cuatro bordes, un poco afuera, mirando hacia adentro
    // (con un desvío, para que no crucen todas derecho).
    const borde = Math.floor(azar() * 4) % 4;
    const desvio = (azar() - 0.5) * 1.4;
    const afuera = a.tam * 2;
    if (borde === 0) Object.assign(a, { x: -afuera, rumbo: desvio });
    else if (borde === 1) Object.assign(a, { x: ancho + afuera, rumbo: Math.PI + desvio });
    else if (borde === 2) Object.assign(a, { y: -afuera, rumbo: Math.PI / 2 + desvio });
    else Object.assign(a, { y: alto + afuera, rumbo: -Math.PI / 2 + desvio });
  }
  return a;
}

/* Un paso de tiempo. Devuelve `true` si la araña se fue —salió de la
   pantalla, o la colgada terminó de subir— y hay que reemplazarla. */
export function moverArana(a, dt, ancho, alto, t) {
  const fuera = a.tam * 3;
  if (a.colgada) {
    const antes = a.y;
    if (a.fase === "baja") {
      // Baja soltando hilo: las patas quietas, encogidas.
      a.y = Math.min(a.fondo, a.y + a.velMax * 0.9 * dt);
      if (a.y >= a.fondo) a.fase = "quieta";
    } else if (a.fase === "quieta") {
      a.espera -= dt;
      if (a.espera <= 0) a.fase = "sube";
    } else {
      // Sube trepando por el hilo: ahí sí mueve las patas.
      a.y -= a.velMax * 1.1 * dt;
      a.paso += (Math.abs(antes - a.y) / (a.tam * 0.9)) * Math.PI * 2;
    }
    a.rumbo = a.fase === "sube" ? -Math.PI / 2 : Math.PI / 2;
    // Se hamaca apenas, y más cuanto más hilo tiene.
    a.x = a.hiloX + Math.sin(t * 0.8 + a.curva[2]) * Math.min(12, Math.max(0, a.y) * 0.03);
    return (a.fase === "sube" && a.y < -fuera) || a.hiloX > ancho + fuera;
  }

  // A los tirones: un tramo andando y una pausa, con largos que salen de
  // `suerte` para que cada araña tenga los suyos.
  if (a.quieta > 0) a.quieta -= dt;
  else if ((a.andando -= dt) <= 0) {
    a.quieta = 0.4 + suerte(a.semilla + a.tramos * 2) * 1.8;
    a.andando = 1.2 + suerte(a.semilla + a.tramos * 2 + 1) * 4;
    a.tramos++;
  }
  const objetivo = a.quieta > 0 ? 0 : a.velMax;
  // Arranca y frena enseguida, pero no de golpe.
  a.vel += (objetivo - a.vel) * Math.min(1, dt * 6);
  const [r1, r2, f1, f2] = a.curva;
  a.rumbo += (Math.sin(t * r1 + f1) * 0.6 + Math.sin(t * r2 + f2) * 0.35) * dt * (a.vel / a.velMax);
  const d = a.vel * dt;
  a.x += Math.cos(a.rumbo) * d;
  a.y += Math.sin(a.rumbo) * d;
  // Un paso entero (las dos tandas) cubre casi el largo de la araña.
  a.paso += (d / (a.tam * 0.9)) * Math.PI * 2;
  return a.x < -fuera || a.x > ancho + fuera || a.y < -fuera || a.y > alto + fuera;
}

/* Dibuja una araña. Todo en las coordenadas de la araña —el frente hacia +x—,
   así que la misma cuenta sirve para cualquier rumbo. `escala` es la del
   lienzo (la densidad de la pantalla). */
/* LA ARAÑA DE FOTO. Igna (09/10) pasó una araña en ocho poses de caminata
 * (4×2, vista de arriba, con la cabeza hacia arriba; `aranas.webp`). Con la
 * plancha cargada se estampa el cuadro que toca según cuánto caminó —`a.paso`
 * ya avanza con la distancia, así que quieta no mueve las patas—; sin ella
 * queda la de código, que es también lo que prueban los tests de más abajo.
 * Cada cuadro está centrado en el lomo, para que el cuerpo no salte. */
export const CUADROS_ARANA = 8;
export const cuadroDeArana = (paso) =>
  ((Math.floor((paso / (Math.PI * 2)) * CUADROS_ARANA) % CUADROS_ARANA) + CUADROS_ARANA) % CUADROS_ARANA;

export function dibujarArana(g, a, escala, color, plancha = null) {
  const s = a.tam;
  const cos = Math.cos(a.rumbo);
  const sen = Math.sin(a.rumbo);
  g.strokeStyle = color;
  g.fillStyle = color;
  g.lineCap = "round";
  g.lineJoin = "round";
  if (a.colgada) {
    // El hilo, desde arriba de todo hasta la cola.
    g.setTransform(escala, 0, 0, escala, 0, 0);
    g.globalAlpha = a.alfa * 0.55;
    g.lineWidth = 0.7;
    g.beginPath();
    g.moveTo(a.hiloX, 0);
    g.lineTo(a.x - cos * s * 0.62, a.y - sen * s * 0.62);
    g.stroke();
  }
  g.globalAlpha = a.alfa;
  g.setTransform(escala * cos, escala * sen, -escala * sen, escala * cos, a.x * escala, a.y * escala);
  if (plancha) {
    const lado = plancha.width / 4;
    const cuadro = cuadroDeArana(a.paso);
    const tam = s * 2.7;
    // La foto mira hacia arriba y acá «adelante» es hacia la derecha.
    g.rotate(Math.PI / 2);
    g.drawImage(
      plancha,
      (cuadro % 4) * lado, Math.floor(cuadro / 4) * lado, lado, lado,
      -tam / 2, -tam / 2, tam, tam,
    );
    g.setTransform(escala, 0, 0, escala, 0, 0);
    g.globalAlpha = 1;
    return;
  }
  // Bajando por el hilo lleva las patas encogidas.
  const encoge = a.colgada && a.fase !== "sube" ? 0.78 : 1;
  const cx = s * 0.1;
  g.lineWidth = Math.max(0.7, s * 0.065);
  g.beginPath();
  for (const lado of [1, -1]) {
    for (const p of PATAS) {
      const fase = a.paso + ((p.tanda + (lado < 0 ? 1 : 0)) % 2) * Math.PI;
      const ang = p.angulo + Math.sin(fase) * BRAZADA;
      const max = s * p.largo * encoge;
      // La pata que va por el aire llega un poco menos.
      const r = max * (1 - 0.1 * Math.max(0, Math.cos(fase)));
      const hx = cx + Math.cos(p.angulo) * s * 0.08;
      const hy = lado * Math.sin(p.angulo) * s * 0.08;
      // La rodilla acompaña al pie, pero menos.
      const angRodilla = p.rodilla + Math.sin(fase) * BRAZADA * 0.5;
      const kx = cx + Math.cos(angRodilla) * max * 0.46;
      const ky = lado * Math.sin(angRodilla) * max * 0.46;
      g.moveTo(hx, hy);
      g.lineTo(kx, ky);
      g.lineTo(cx + Math.cos(ang) * r, lado * Math.sin(ang) * r);
    }
    // Los palpos, dos patitas cortas adelante.
    g.moveTo(s * 0.24, lado * s * 0.05);
    g.lineTo(s * 0.36, lado * s * 0.1);
  }
  g.stroke();
  // El cuerpo: el abdomen atrás y la cabeza adelante.
  g.beginPath();
  g.ellipse(-s * 0.34, 0, s * 0.33, s * 0.25, 0, 0, Math.PI * 2);
  g.moveTo(cx + s * 0.19, 0);
  g.ellipse(cx, 0, s * 0.19, s * 0.155, 0, 0, Math.PI * 2);
  g.fill();
  // Las de adelante llevan un brillo en el lomo: es lo que les da volumen.
  if (a.z > 0.6) {
    g.globalAlpha = a.alfa * 0.2;
    g.fillStyle = "#ffffff";
    g.beginPath();
    g.ellipse(-s * 0.4, -s * 0.09, s * 0.15, s * 0.05, 0, 0, Math.PI * 2);
    g.fill();
  }
  g.setTransform(escala, 0, 0, escala, 0, 0);
  g.globalAlpha = 1;
}

/* ── El simbionte ────────────────────────────────────────────────────────────
 *
 * Lautaro (Satelital, 08/10), por mail a Cepi, después de ver Spider-Man:
 * «¿podrás hacer la contraparte? tipo uno que tenga que ver con Venom? Que
 * tenga detalles del simbionte».
 *
 * La regla es la de siempre: el material, no el personaje. Acá no hay cara,
 * ni boca, ni la araña del pecho: es goo negro, cualquiera, dibujado con
 * curvas.
 *
 * ── QUÉ LO HACE CREÍBLE ─────────────────────────────────────────────────────
 *
 *   Los zarcillos  asoman desde un borde de la pantalla, despacio y cada vez
 *                  más despacio (como si les costara), se quedan un rato
 *                  ondulando y se vuelven a meter, un poco más rápido. Son
 *                  gruesos en la base —donde hay una mancha que sale de abajo
 *                  del borde— y terminan en punta. Ondulan de la base a la
 *                  punta: la base quieta, la punta es la que busca.
 *   El brillo      el goo es NEGRO sobre un fondo casi negro: lo que lo dibuja
 *                  es la luz. Un filo frío (azulado, violeta) del lado de
 *                  arriba, como el reflejo de algo mojado, y un destello
 *                  blanco corto cerca de la base.
 *   Las venas      la mayoría lleva una vena blanca fina que corre por adentro
 *                  y se abre en una rama.
 *   Las gotas      de un zarcillo quieto cuelga cada tanto una gota: se
 *                  estira de a poco con su cuello y, cuando ya pesa (o el
 *                  zarcillo se retrae), se suelta y cae, espesa: con gravedad,
 *                  pero sin pasar de una velocidad.
 *
 * Un zarcillo son dos rellenos (el cuerpo con su mancha, y el lustre) y tres
 * trazos (el halo que lo separa del fondo, el filo y lo blanco), por cuadro;
 * una gota, un relleno, un trazo y un brillo. Ocho zarcillos y cuatro gotas
 * cuestan casi lo mismo que las diez arañas (medido en Chromium a 1440×900:
 * 0,19 ms por cuadro con el halo, 0,18 sin él, contra 0,13 de las arañas).
 * Guardan estado entre cuadros como las arañas, y el desorden sale de `azar`
 * al nacer y de `suerte` después — se pueden probar con una secuencia fija. */

// Cuántos tramos tiene el espinazo de un zarcillo.
const TRAMOS = 14;
// De qué lado viene la luz: de arriba a la izquierda.
const LUZ = [-0.6, -0.8];
// Lo que tiene de color. El cuerpo es negro de verdad; lo demás es luz.
export const TINTA_SIMBIONTE = {
  goo: "#000000",
  halo: "rgba(120,128,255,0.14)",
  lustre: "rgba(118,128,226,0.24)",
  filo: "rgba(168,178,255,0.75)",
  blanco: "rgba(242,237,227,0.55)",
  destello: "rgba(255,255,255,0.75)",
  // Sólo la mancha grande: la luz que rebota del lado de la sombra.
  rebote: "rgba(150,140,255,0.34)",
  // Sólo las trenzas: el halo ya mezclado con el negro, macizo (ver
  // `pasadaTrenza`).
  corte: "rgb(17,18,36)",
};

/* De qué borde asoma, según un número entre 0 y 1: más de los costados y de
   abajo que de arriba, que en el hub es donde está la barra y no se vería. */
const bordeDe = (u) => (u < 0.34 ? 1 : u < 0.64 ? 3 : u < 0.88 ? 0 : 2);

/* Dónde está la base y hacia dónde mira, recién salido de su borde. Se
   recalcula en cada paso: si la ventana cambia de tamaño, el zarcillo sigue
   pegado a su borde. */
function anclar(zr, ancho, alto) {
  const afuera = zr.grosor * 0.5;
  if (zr.borde === 0) Object.assign(zr, { x: -afuera, y: zr.donde * alto, rumbo: zr.desvio });
  else if (zr.borde === 1) Object.assign(zr, { x: ancho + afuera, y: zr.donde * alto, rumbo: Math.PI + zr.desvio });
  else if (zr.borde === 2) Object.assign(zr, { x: zr.donde * ancho, y: -afuera, rumbo: Math.PI / 2 + zr.desvio });
  else Object.assign(zr, { x: zr.donde * ancho, y: alto + afuera, rumbo: -Math.PI / 2 + zr.desvio });
}

/* Un zarcillo nuevo. `entra` es el que reemplaza a uno que se metió: arranca
   escondido y asoma después de un rato. Los del arranque ya están afuera, a
   medio asomar o quietos, o la pantalla empezaría vacía. */
export function nuevoZarcillo(ancho, alto, azar = Math.random, entra = false) {
  const z = azar();
  const borde = bordeDe(azar());
  const corto = Math.max(1, Math.min(ancho, alto));
  const zr = {
    z,
    borde,
    donde: 0.08 + azar() * 0.84,
    desvio: (azar() - 0.5) * 0.9,
    grosor: entre([15, 30], z),
    // Nunca llega a la mitad de la pantalla: es algo que asoma, no que la
    // cruza. Los de arriba, un poco más largos, que la barra tapa el comienzo.
    largoMax: corto * entre([0.14, 0.3], z) + 30 + (borde === 2 ? 70 : 0),
    largo: 0,
    vel: entre([14, 26], z) * (0.85 + azar() * 0.3),
    // Cuántas ondas le entran a lo largo, a qué ritmo y desde dónde.
    ondas: [0.6 + azar() * 0.8, 0.5 + azar() * 0.7, azar() * Math.PI * 2],
    // Para qué lado se enrosca la punta.
    curva: (azar() - 0.5) * 1.4,
    vena: azar() < 0.6,
    goteo: 2 + azar() * 5,
    semilla: Math.floor(azar() * 1e4),
    gotas: 0,
    gotea: false,
    fase: "asoma",
    espera: 0,
    puntos: null,
    anchos: null,
  };
  anclar(zr, ancho, alto);
  const k = azar();
  if (entra) Object.assign(zr, { fase: "oculto", espera: 0.5 + k * 3 });
  else if (k < 0.5) zr.largo = k * 2 * zr.largoMax;
  else Object.assign(zr, { fase: "queda", largo: zr.largoMax, espera: 1 + (k - 0.5) * 12 });
  return zr;
}

/* El espinazo: los puntos de la base a la punta y el ancho en cada uno. La
   base mira derecho hacia adentro; la onda crece hacia la punta, que es la que
   se mueve, y la punta además se enrosca. Y respira: se estira y se encoge
   apenas, siempre (si respirara sólo quieto, al empezar a meterse daría un
   salto). */
export function espinaZarcillo(zr, t) {
  const respira = 1 + 0.04 * Math.sin(t * 0.9 + zr.ondas[2]);
  const L = zr.largo * respira;
  const pts = zr.puntos || (zr.puntos = new Array((TRAMOS + 1) * 2));
  const an = zr.anchos || (zr.anchos = new Array(TRAMOS + 1));
  // Recién asomado es una mancha chica: no un tubo grueso y cortito.
  const grueso = zr.grosor * (0.4 + 0.6 * Math.min(1, L / (zr.largoMax * 0.6)));
  const [cuantas, ritmo, fase] = zr.ondas;
  const ds = L / TRAMOS;
  let x = zr.x;
  let y = zr.y;
  for (let i = 0; i <= TRAMOS; i++) {
    const u = i / TRAMOS;
    pts[i * 2] = x;
    pts[i * 2 + 1] = y;
    // Fino hacia la punta, con bultos que corren por el cuerpo (está vivo), y
    // ensanchándose al llegar a la base, que se funde con la mancha.
    an[i] =
      grueso * (0.1 + 0.9 * Math.pow(1 - u, 0.75)) * (1 + 0.16 * Math.sin(u * 9 - t * 1.3 + fase)) +
      grueso * 1.1 * Math.pow(Math.max(0, 1 - u / 0.22), 2);
    const ang = zr.rumbo + zr.curva * u * u + Math.sin(u * cuantas * Math.PI * 2 - t * ritmo + fase) * 0.45 * u;
    x += Math.cos(ang) * ds;
    y += Math.sin(ang) * ds;
  }
  return pts;
}

/* Un paso de tiempo. Devuelve `true` cuando el zarcillo terminó de meterse y
   hay que reemplazarlo. `gotea` queda en `true` sólo en el cuadro en que le
   toca soltar una gota (quien lo mueve decide si hay lugar para ella). */
export function moverZarcillo(zr, dt, ancho, alto, t) {
  zr.gotea = false;
  anclar(zr, ancho, alto);
  if (zr.fase === "oculto") {
    zr.espera -= dt;
    if (zr.espera <= 0) zr.fase = "asoma";
  } else if (zr.fase === "asoma") {
    // Cuanto más afuera, más despacio: como si le costara.
    const k = zr.largo / zr.largoMax;
    zr.largo = Math.min(zr.largoMax, zr.largo + zr.vel * (1 - 0.6 * k) * dt);
    if (zr.largo >= zr.largoMax - 0.5) {
      zr.largo = zr.largoMax;
      zr.fase = "queda";
      zr.espera = 3 + suerte(zr.semilla) * 6;
    }
  } else if (zr.fase === "queda") {
    zr.espera -= dt;
    zr.goteo -= dt;
    if (zr.goteo <= 0) {
      zr.gotea = true;
      zr.gotas++;
      zr.goteo = 3 + suerte(zr.semilla + zr.gotas) * 6;
    }
    if (zr.espera <= 0) zr.fase = "vuelve";
  } else {
    // Se mete un poco más rápido de lo que salió.
    zr.largo = Math.max(0, zr.largo - zr.vel * 1.4 * dt);
    if (zr.largo <= 0) return true;
  }
  espinaZarcillo(zr, t);
  return false;
}

/* Dibuja un zarcillo: el halo y el cuerpo con su mancha (un trazo y un
   relleno del mismo contorno), el lustre en otro relleno, el filo de luz en
   un trazo y lo blanco (el destello y la vena) en otro. */
export function dibujarZarcillo(g, zr, tinta = TINTA_SIMBIONTE) {
  if (zr.fase === "oculto" || zr.largo < 0.5 || !zr.puntos) return;
  const p = zr.puntos;
  const an = zr.anchos;
  const N = TRAMOS;
  // La normal en cada punto (girando la dirección del espinazo 90°).
  const nx = (i) => {
    const a = Math.max(0, i - 1);
    const b = Math.min(N, i + 1);
    const dx = p[b * 2] - p[a * 2];
    const dy = p[b * 2 + 1] - p[a * 2 + 1];
    const d = Math.hypot(dx, dy) || 1;
    return [-dy / d, dx / d];
  };
  const normales = [];
  for (let i = 0; i <= N; i++) normales.push(nx(i));
  const borde = (i, lado, adentro = 0) => [
    p[i * 2] + normales[i][0] * lado * (an[i] / 2 - adentro),
    p[i * 2 + 1] + normales[i][1] * lado * (an[i] / 2 - adentro),
  ];

  // El cuerpo: un lado de la base a la punta y el otro de vuelta, con curvas
  // por los puntos medios para que no tenga quiebres.
  g.globalAlpha = 0.94;
  g.fillStyle = tinta.goo;
  g.beginPath();
  let [x0, y0] = borde(0, 1);
  g.moveTo(x0, y0);
  for (let i = 1; i <= N; i++) {
    const [x, y] = borde(i, 1);
    g.quadraticCurveTo(x0, y0, (x0 + x) / 2, (y0 + y) / 2);
    [x0, y0] = [x, y];
  }
  // La punta, redonda: apenas más adelante del último punto del espinazo.
  const fx = p[N * 2] - p[(N - 1) * 2];
  const fy = p[N * 2 + 1] - p[(N - 1) * 2 + 1];
  const fd = Math.hypot(fx, fy) || 1;
  g.quadraticCurveTo(x0, y0, p[N * 2] + (fx / fd) * an[N], p[N * 2 + 1] + (fy / fd) * an[N]);
  [x0, y0] = borde(N, -1);
  for (let i = N - 1; i >= 0; i--) {
    const [x, y] = borde(i, -1);
    g.quadraticCurveTo(x0, y0, (x0 + x) / 2, (y0 + y) / 2);
    [x0, y0] = [x, y];
  }
  g.closePath();
  // La mancha de la base: dos bultos que salen de abajo del borde. Van en el
  // mismo sentido de giro que el cuerpo, o el relleno los restaría.
  const k = Math.min(1, zr.largo / zr.largoMax);
  const r = zr.grosor * (0.7 + 0.5 * k);
  const [n0x, n0y] = normales[0];
  g.moveTo(zr.x + r, zr.y);
  g.arc(zr.x, zr.y, r, Math.PI * 2, 0, true);
  const bx = zr.x + n0x * r * 0.9;
  const by = zr.y + n0y * r * 0.9;
  g.moveTo(bx + r * 0.7, by);
  g.arc(bx, by, r * 0.7, Math.PI * 2, 0, true);
  // El halo: el mismo contorno, trazado ancho y muy tenue en el frío del
  // reflejo, ANTES del relleno (que tapa la mitad de adentro). Negro sobre un
  // fondo casi negro es 1,05 a 1: sin esto, de la masa sólo se veían el filo
  // y la vena, y los zarcillos parecían alambres violetas y no goo. Un trazo
  // más por zarcillo y sin `shadowBlur`, que es lo que cuesta.
  g.globalAlpha = 1;
  g.strokeStyle = tinta.halo;
  g.lineWidth = 6;
  g.lineJoin = "round";
  g.stroke();
  g.globalAlpha = 0.94;
  g.fill();

  // El lado que da a la luz. Primero el lustre, una franja apagada adentro
  // del cuerpo que se afina con él y le da volumen; después el filo, la línea
  // fina y fría del borde, que da la vuelta por la punta y vuelve un poco por
  // el otro lado: es lo que dibuja la silueta contra el fondo negro.
  const lado = n0x * LUZ[0] + n0y * LUZ[1] > 0 ? 1 : -1;
  const hasta = Math.round(N * 0.78);
  g.fillStyle = tinta.lustre;
  g.beginPath();
  for (let i = 1; i <= hasta; i++) {
    const [x, y] = borde(i, lado, an[i] * 0.1);
    if (i === 1) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  for (let i = hasta; i >= 1; i--) {
    const [x, y] = borde(i, lado, an[i] * 0.42);
    g.lineTo(x, y);
  }
  g.closePath();
  g.fill();
  g.globalAlpha = 1;
  g.lineCap = "round";
  g.lineJoin = "round";
  g.strokeStyle = tinta.filo;
  g.lineWidth = 1.4;
  g.beginPath();
  for (let i = 1; i <= N; i++) {
    const [x, y] = borde(i, lado, 1);
    if (i === 1) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  for (let i = N; i >= Math.round(N * 0.55); i--) {
    const [x, y] = borde(i, -lado, 1);
    g.lineTo(x, y);
  }
  g.stroke();

  // Lo blanco: un destello corto cerca de la base, del lado de la luz, y la
  // vena, que corre por adentro y se abre en una rama.
  g.strokeStyle = tinta.blanco;
  g.lineWidth = 1.1;
  g.beginPath();
  const desde = Math.max(1, Math.round(N * 0.12));
  const hastaDestello = Math.round(N * 0.38);
  for (let i = desde; i <= hastaDestello; i++) {
    const x = p[i * 2] + normales[i][0] * lado * an[i] * 0.22;
    const y = p[i * 2 + 1] + normales[i][1] * lado * an[i] * 0.22;
    if (i === desde) g.moveTo(x, y);
    else g.lineTo(x, y);
  }
  if (zr.vena) {
    const fase = zr.ondas[2];
    const desdeVena = Math.round(N * 0.18);
    const hastaVena = Math.round(N * 0.88);
    for (let i = desdeVena; i <= hastaVena; i++) {
      const corre = -lado * an[i] * 0.18 * Math.sin(i * 0.9 + fase);
      const x = p[i * 2] + normales[i][0] * corre;
      const y = p[i * 2 + 1] + normales[i][1] * corre;
      if (i === desdeVena) g.moveTo(x, y);
      else g.lineTo(x, y);
    }
    // La rama: sale de la mitad hacia el borde de la sombra.
    const m = Math.round(N * 0.5);
    const [rx, ry] = borde(Math.min(N, m + 2), -lado, an[m] * 0.15);
    g.moveTo(p[m * 2], p[m * 2 + 1]);
    g.quadraticCurveTo(p[(m + 1) * 2], p[(m + 1) * 2 + 1], rx, ry);
  }
  g.stroke();
  g.globalAlpha = 1;
}

/* Una gota que empieza a colgar de un zarcillo, de algún lugar entre la mitad
   y la punta. Tarda unos segundos en formarse. */
export function nuevaGota(zr, azar = Math.random) {
  return {
    zarcillo: zr,
    i: Math.round(TRAMOS * (0.45 + azar() * 0.4)),
    fase: "cuelga",
    t: 0,
    forma: 3 + azar() * 3,
    cuelloMax: 10 + azar() * 16,
    rMax: Math.max(2.5, zr.grosor * (0.22 + azar() * 0.12)),
    r: 0,
    cuello: 0,
    ax: 0,
    ay: 0,
    x: 0,
    y: 0,
    vy: 0,
  };
}

/* Un paso de tiempo. Devuelve `true` cuando la gota ya cayó fuera de la
   pantalla. Colgada, sigue al zarcillo (que ondula); se suelta cuando terminó
   de formarse o cuando el zarcillo se empieza a meter. */
export function moverGota(gt, dt, alto) {
  if (gt.fase === "cuelga") {
    const zr = gt.zarcillo;
    if (zr.puntos) {
      gt.ax = zr.puntos[gt.i * 2];
      gt.ay = zr.puntos[gt.i * 2 + 1] + zr.anchos[gt.i] * 0.3;
    }
    gt.t += dt;
    const k = Math.min(1, gt.t / gt.forma);
    gt.r = gt.rMax * (0.35 + 0.65 * k);
    gt.cuello = gt.cuelloMax * k * k;
    gt.x = gt.ax;
    gt.y = gt.ay + gt.cuello + gt.r;
    if (k >= 1 || zr.fase === "vuelve" || zr.fase === "oculto") {
      gt.fase = "cae";
      gt.vy = 8;
    }
    return false;
  }
  // Espesa: cae con gravedad, pero no pasa de una velocidad.
  gt.vy = Math.min(170, gt.vy + 260 * dt);
  gt.y += gt.vy * dt;
  return gt.y - gt.r * 4 > alto;
}

/* Dibuja una gota: colgada, con el cuello que la une al zarcillo; cayendo,
   estirada hacia arriba según lo rápido que va. Un relleno, el filo de luz y
   un brillo. */
export function dibujarGota(g, gt, tinta = TINTA_SIMBIONTE) {
  const { x, y, r } = gt;
  if (r <= 0) return;
  g.globalAlpha = 0.95;
  g.fillStyle = tinta.goo;
  g.beginPath();
  if (gt.fase === "cuelga") {
    const arriba = r * 0.85;
    const cuello = r * 0.3;
    const medio = (gt.ay + y) / 2;
    g.moveTo(gt.ax - arriba, gt.ay);
    g.quadraticCurveTo(x - cuello, medio, x - r * 0.94, y - r * 0.35);
    g.arc(x, y, r, Math.PI + 0.36, -0.36, true);
    g.quadraticCurveTo(x + cuello, medio, gt.ax + arriba, gt.ay);
  } else {
    const cola = r * (0.8 + Math.min(1.6, gt.vy / 80));
    g.moveTo(x, y - r - cola);
    g.quadraticCurveTo(x + r * 1.05, y - r * 0.55, x + r, y);
    g.arc(x, y, r, 0, Math.PI, false);
    g.quadraticCurveTo(x - r * 1.05, y - r * 0.55, x, y - r - cola);
  }
  g.closePath();
  g.fill();
  g.globalAlpha = 1;
  g.strokeStyle = tinta.filo;
  g.lineWidth = 1;
  g.beginPath();
  g.arc(x, y, Math.max(0.5, r - 0.8), Math.PI * 0.95, Math.PI * 1.45);
  g.stroke();
  g.fillStyle = tinta.destello;
  g.beginPath();
  g.ellipse(x - r * 0.35, y - r * 0.3, r * 0.26, r * 0.16, -0.6, 0, Math.PI * 2);
  g.fill();
}

/* ── La mancha ───────────────────────────────────────────────────────────────
 *
 * Lautaro (Satelital, 09/10) pidió la cara de Venom de fondo. La cara no va:
 * es un dibujo con dueño, la misma regla que los escudos de los clubes. Se le
 * ofreció en cambio «darle más presencia al fondo: una mancha grande de
 * simbionte que se mueve despacio detrás de la pantalla, sin cara», y
 * contestó «sisi porfa sumala». Es eso: UNA mancha grande de goo negro
 * brillante que pasea despacio detrás del contenido y va cambiando de forma.
 * Sin ojos, sin boca, sin dientes, sin la araña del pecho: una masa y nada
 * más.
 *
 * ── QUÉ LA HACE GOO Y NO UN CÍRCULO OSCURO ──────────────────────────────────
 *
 *   El borde      nunca es redondo: cuatro pliegues lentos (de dos a cinco
 *                 bultos por vuelta) que giran cada uno a su ritmo, así que la
 *                 silueta se rehace sola y no se repite.
 *   Los brazos    tres lóbulos que se estiran hacia afuera y se vuelven a
 *                 meter, cada uno a su ritmo.
 *   Los pedazos   dos bolitas de la misma masa que se alejan y vuelven:
 *                 salen estirando un cuello que se va afinando, y la mancha
 *                 las vuelve a tragar. Es lo que más dice «goo»: dos círculos
 *                 negros que se tocan son dos círculos; unidos por un cuello
 *                 son algo espeso. Por eso no se sueltan nunca del todo: una
 *                 bolita suelta, con la mancha tapada por una tabla, era un
 *                 círculo negro cualquiera en el medio de la pantalla. Y por
 *                 lo mismo salen para abajo y para los costados, nunca para
 *                 arriba: arriba están el título y las tablas, que le tapan
 *                 el cuello, y lo que asoma es otra vez un círculo suelto.
 *   El brillo     es negro sobre casi negro: la dibuja la luz, como a los
 *                 zarcillos y con la misma luz (de arriba a la izquierda). Un
 *                 resplandor frío alrededor que se apaga de a poco, un lustre
 *                 violeta del lado de la luz —es lo que la hace abombada—, una
 *                 raya hueso fina adentro de ese borde, el filo frío del borde
 *                 que se apaga hacia la sombra y, del lado de la sombra, un
 *                 rebote violeta tenue, que es lo que tiene lo mojado.
 *   Las venas     dos, finas, que la cruzan sin juntarse en el medio: venas
 *                 que salieran de un centro se leerían como una araña.
 *   El paseo      va y viene por la pantalla a unos pocos píxeles por segundo
 *                 y no se sale nunca: si un brazo va a tocar el borde, se
 *                 corre la mancha entera; un pedazo que no tiene lugar para
 *                 alejarse se queda pegado.
 *
 * ── LO QUE NO HACE ──────────────────────────────────────────────────────────
 *
 * No tapa nada: va en el mismo lienzo que los zarcillos, DETRÁS del contenido,
 * y es negra —al lado del texto hueso sólo suma contraste—; la luz que tiene
 * es tenue y fina. Lo blanco pintado lleno es una sola raya, siempre del lado
 * de la luz: dos manchas blancas sobre una masa negra serían unos ojos. Lo
 * otro claro son las venas: líneas de un píxel, a media luz, encima de su
 * brillo violeta, que nunca van hacia donde viene la luz. Con «menos
 * movimiento» no está, como el resto del ambiente: queda el fondo quieto, que
 * ya trae sus dos manchas en las esquinas.
 *
 * Y cuesta poco a propósito: un contorno de 64 puntos y dos círculos, sin
 * `filter` ni `shadowBlur` (desenfocar por cuadro un lienzo de pantalla entera
 * es lo que calienta una PC de oficina). Lo que parece un resplandor son dos
 * trazos anchos y tenues del mismo contorno. Son nueve pasadas (cinco trazos
 * y cuatro rellenos), pero no en cada cuadro: se dibuja en un lienzo aparte uno
 * de cada tres, y en los demás se estampa ese dibujo (ver `estamparMancha`).
 * En una PC que ni así da abasto deja de cambiar de forma y sólo pasea, y si
 * tampoco así, se va (ver «EL PISO» en `crearAmbiente`). No guarda estado:
 * forma y lugar salen del reloj, como en las escenas de los temas de foto, y
 * el desorden sale de `azar` al nacer; se puede probar pidiéndole un
 * instante. */

// Cuántos puntos tiene el contorno.
const PUNTOS_MANCHA = 64;
// Lo más que se estira y lo más que se encoge el borde, en radios. Ni con
// dos brazos encimados pasa de ahí: se frena de a poco, no de golpe.
export const MANCHA_MAX = 1.6;
const MANCHA_MIN = 0.62;
// Lo que queda libre entre la mancha y el borde de la pantalla: que tampoco
// se corte el resplandor (el trazo más ancho mide 36).
export const MANCHA_AIRE = 20;
// El resplandor: dos anchos que se suman, para que se apague de a poco.
const RESPLANDOR = [36, 14];
// Hasta dónde llegaría el cuello de un pedazo antes de cortarse, en radios
// del pedazo más allá del borde de la mancha, y hasta dónde sale el pedazo:
// antes. Así el cuello más fino sigue siendo un cuello y no un pelo.
export const PEDAZO_HILO = 2.8;
export const PEDAZO_LEJOS = 2.3;

/* El radio de la mancha: grande —un quinto largo de la pantalla—, pero que
   entre entera aun con un brazo estirado, también en un celular parado. */
export function radioMancha(ancho, alto) {
  const corto = Math.max(1, Math.min(ancho, alto));
  return Math.max(
    4,
    Math.min(0.19 * Math.sqrt(Math.max(1, ancho * alto)), 360, (corto - 2 * MANCHA_AIRE) / (2 * MANCHA_MAX)),
  );
}

/* Una mancha nueva: sólo sus ritmos y sus fases. Dónde está y qué forma tiene
   en cada instante lo dice `formaMancha`. */
export function nuevaMancha(azar = Math.random) {
  const sentido = () => (azar() < 0.5 ? -1 : 1);
  const entera = Math.PI * 2;
  const brazo0 = azar() * entera;
  // Para dónde salen los pedazos (ver abajo): el mediano, abajo y corrido
  // hacia un lado; el chico, al costado del otro lado, apenas bajo la
  // horizontal. Del lado de la izquierda es el mismo ángulo, en espejo.
  const mediano = 1.9 + azar() * 0.35;
  const lado = sentido();
  const chico = 0.15 + azar() * 0.2;
  const espejo = (a) => (lado > 0 ? a : Math.PI - a);
  return {
    // Los de pocos bultos pesan más (dan la forma); los otros, el temblor.
    pliegues: [2, 3, 4, 5].map((k, i) => ({
      k,
      alto: [0.085, 0.06, 0.04, 0.025][i] * (0.75 + azar() * 0.5),
      vel: sentido() * (0.05 + azar() * 0.08),
      fase: azar() * entera,
    })),
    // Repartidos por la vuelta (con un desvío) y corriéndose despacio.
    brazos: [0, 1, 2].map((i) => ({
      angulo: brazo0 + (i * entera) / 3 + (azar() - 0.5) * 0.9,
      deriva: (azar() - 0.5) * 0.05,
      largo: 0.3 + azar() * 0.25,
      ancho: 0.24 + azar() * 0.1,
      ritmo: 0.1 + azar() * 0.08,
      fase: azar() * entera,
    })),
    // Los dos pedazos: uno mediano, que tira hacia abajo (es espeso: lo que
    // se separa, cuelga), y uno chico hacia un costado, siempre lejos del
    // otro: encimados, uno quedaba metido en el cuello del otro. NINGUNO
    // PARA ARRIBA: el chico salía arriba ocho de cada diez veces —«abajo»
    // más dos radianes y pico era arriba a un lado o al otro—, justo donde
    // el título y las tablas le tapan el cuello, y lo que se veía era un
    // círculo negro suelto con su filo violeta al lado del botón de
    // «+ NUEVO VIAJE» (revisión del 09/10). Con su vaivén (`mece`) incluido,
    // el mediano anda entre 92° y 146° (0° es a la derecha y 90°, derecho
    // abajo), o en espejo, y el chico entre −3° y 32° del otro lado: a lo
    // sumo 3° arriba de la horizontal. Entre los dos quedan siempre más de
    // 57° (un radián).
    pedazos: [0, 1].map((i) => ({
      tam: i === 0 ? 0.2 + azar() * 0.04 : 0.12 + azar() * 0.03,
      angulo: espejo(i === 0 ? mediano : chico),
      mece: i === 0 ? 0.3 : 0.2,
      // Una vuelta entera —salir estirando el cuello y volver— tarda casi un
      // minuto.
      ritmo: 0.09 + azar() * 0.04,
      fase: azar() * entera,
      vaiven: azar() * entera,
      x: 0,
      y: 0,
      r: 0,
      d: 0,
      ang: 0,
      fuera: false,
      cuello: false,
      // El cuello: los dos puntos donde toca la mancha (1, 2), los dos donde
      // toca el pedazo (3, 4) y los controles de las curvas (h).
      p: new Float64Array(16),
      // De qué ángulo a qué ángulo de la mancha se agarra.
      agarre: [0, 0],
    })),
    // El paseo: dos senos por eje, de ritmos que no se acompañan.
    paseo: [azar() * entera, azar() * entera, azar() * entera, azar() * entera],
    // Las venas, en la mancha y no en la pantalla: un tramo de vuelta (desde
    // qué ángulo, cuánto abarca) y a qué fracción del radio va al empezar y
    // al terminar. Una abajo a la derecha y la otra a la izquierda: nunca por
    // el centro ni hacia la luz. La de la izquierda llegaba hasta 4,1 rad,
    // que es justo de donde viene la luz (revisión del 09/10): ahora termina
    // a 3,4 como mucho —a la izquierda, apenas arriba del medio—, a más de
    // 38° de la luz y lejos de la raya hueso.
    venas: [
      { desde: 0.05 + azar() * 0.6, abarca: 1.1 + azar() * 0.5, de: 0.42 + azar() * 0.12, a: 0.8, fase: azar() * entera },
      { desde: 2.3 + azar() * 0.3, abarca: 0.6 + azar() * 0.2, de: 0.76, a: 0.5 + azar() * 0.1, fase: azar() * entera },
    ],
    x: 0,
    y: 0,
    R: 0,
    t: 0,
    puntos: new Float64Array(PUNTOS_MANCHA * 2),
    normales: new Float64Array(PUNTOS_MANCHA * 2),
    // Cuánto mira cada punto del borde hacia la luz, de -1 a 1.
    luz: new Float64Array(PUNTOS_MANCHA),
    // Para los contornos corridos hacia adentro (la raya hueso, el filo): se
    // reusa.
    aux: new Float64Array(PUNTOS_MANCHA * 2),
  };
}

// La distancia entre dos ángulos, de -π a π.
const giroEntre = (a, b) => {
  const d = (a - b) % (Math.PI * 2);
  return d > Math.PI ? d - Math.PI * 2 : d < -Math.PI ? d + Math.PI * 2 : d;
};
const acotar = (v, a, b) => Math.min(b, Math.max(a, v));

/* Cuánto mide la mancha hacia el ángulo `ang`, en radios, en el instante `t`.
   Los pliegues y los brazos se suman, y lo que pasa de MANCHA_MAX (o queda
   por debajo de MANCHA_MIN) se va frenando suave, sin un tope que la aplane. */
export function factorMancha(m, ang, t) {
  let f = 1;
  for (const p of m.pliegues) f += p.alto * Math.sin(p.k * ang + p.fase + p.vel * t);
  for (const b of m.brazos) {
    // Se estira y se vuelve a meter; afuera del todo dura poco y adentro
    // descansa un rato (por eso el cuadrado).
    const s = 0.5 - 0.5 * Math.cos(b.ritmo * t + b.fase);
    const d = giroEntre(ang, b.angulo + b.deriva * t) / b.ancho;
    f += b.largo * s * s * Math.exp(-d * d);
  }
  if (f > 1) return 1 + (MANCHA_MAX - 1) * Math.tanh((f - 1) / (MANCHA_MAX - 1));
  return 1 - (1 - MANCHA_MIN) * Math.tanh((1 - f) / (1 - MANCHA_MIN));
}

/* El cuello entre la mancha y un pedazo: la cuenta de siempre de las
   «metaballs» (dos círculos unidos por dos curvas que se abren hacia cada
   uno), con la mancha tomada como un círculo del radio que tiene hacia el
   pedazo, pero con los dos puntos de agarre puestos sobre su borde de
   verdad. Pasado el cuerpo a cuerpo, el cuello se va afinando con la
   distancia hasta ser un hilo, y ahí se corta: no desaparece de golpe. */
function cuelloPedazo(m, pz, t) {
  const R = m.R;
  const r1 = R * factorMancha(m, pz.ang, t);
  const r2 = pz.r;
  const d = pz.d;
  const lejos = r1 + r2 * PEDAZO_HILO;
  pz.cuello = d < lejos && d + r2 > r1;
  if (!pz.cuello) return;
  let v = 0.5;
  if (d > r1 + r2) v *= Math.pow((lejos - d) / (lejos - r1 - r2), 0.7);
  const tocan = d < r1 + r2;
  const u1 = tocan ? Math.acos(acotar((r1 * r1 + d * d - r2 * r2) / (2 * r1 * d), -1, 1)) : 0;
  const u2 = tocan ? Math.acos(acotar((r2 * r2 + d * d - r1 * r1) / (2 * r2 * d), -1, 1)) : 0;
  const a = pz.ang;
  const abre = Math.acos(acotar((r1 - r2) / d, -1, 1));
  const a1 = a + u1 + (abre - u1) * v;
  const a2 = a - u1 - (abre - u1) * v;
  const a3 = a + Math.PI - u2 - (Math.PI - u2 - abre) * v;
  const a4 = a - Math.PI + u2 + (Math.PI - u2 - abre) * v;
  const ra = R * factorMancha(m, a1, t);
  const rb = R * factorMancha(m, a2, t);
  const p = pz.p;
  p[0] = m.x + Math.cos(a1) * ra;
  p[1] = m.y + Math.sin(a1) * ra;
  p[2] = m.x + Math.cos(a2) * rb;
  p[3] = m.y + Math.sin(a2) * rb;
  p[4] = pz.x + Math.cos(a3) * r2;
  p[5] = pz.y + Math.sin(a3) * r2;
  p[6] = pz.x + Math.cos(a4) * r2;
  p[7] = pz.y + Math.sin(a4) * r2;
  const juntos = r1 + r2;
  const d2 = Math.min(v * 2.4, Math.hypot(p[4] - p[0], p[5] - p[1]) / juntos) * Math.min(1, (d * 2) / juntos);
  const h = (i, x, y, ang, largo) => {
    p[i] = x + Math.cos(ang) * largo;
    p[i + 1] = y + Math.sin(ang) * largo;
  };
  h(8, p[0], p[1], a1 - Math.PI / 2, r1 * d2);
  h(10, p[2], p[3], a2 + Math.PI / 2, r1 * d2);
  h(12, p[4], p[5], a3 + Math.PI / 2, r2 * d2);
  h(14, p[6], p[7], a4 - Math.PI / 2, r2 * d2);
  pz.a3 = a3;
  pz.a4 = a4;
  pz.agarre[0] = a2;
  pz.agarre[1] = a1;
}

/* La mancha en el instante `t`, en una pantalla de `ancho`×`alto`: deja en
   `m` el centro, el radio, los puntos del borde, sus normales, cuánto mira
   cada uno hacia la luz y dónde están los pedazos. Devuelve la misma `m`. */
export function formaMancha(m, t, ancho, alto, forma = t) {
  const N = PUNTOS_MANCHA;
  const R = radioMancha(ancho, alto);
  const [f1, f2, f3, f4] = m.paseo;
  // A 1440×900 no pasa de unos 10 px por segundo; una vuelta entera del
  // paseo tarda varios minutos. No va por toda la pantalla parejo: anda más
  // por abajo, que es donde las pantallas del hub suelen tener lugar (arriba
  // están el título y las tablas, y ahí se veía sólo la mitad de la mancha), y
  // un poco más a la derecha, que a la izquierda está el menú.
  const ux = 0.72 * Math.sin(t * 0.017 + f1) + 0.28 * Math.sin(t * 0.041 + f2);
  const uy = 0.7 * Math.sin(t * 0.023 + f3) + 0.3 * Math.sin(t * 0.053 + f4);
  const libreX = Math.max(0, ancho / 2 - R * 1.1 - MANCHA_AIRE);
  const libreY = Math.max(0, alto / 2 - R * 1.1 - MANCHA_AIRE);
  let cx = ancho / 2 + libreX * (0.15 + 0.85 * ux);
  let cy = alto / 2 + libreY * (0.3 + 0.7 * uy);
  const p = m.puntos;
  let x0 = Infinity;
  let x1 = -Infinity;
  let y0 = Infinity;
  let y1 = -Infinity;
  for (let i = 0; i < N; i++) {
    const ang = (i / N) * Math.PI * 2;
    const r = R * factorMancha(m, ang, forma);
    const x = Math.cos(ang) * r;
    const y = Math.sin(ang) * r;
    p[i * 2] = x;
    p[i * 2 + 1] = y;
    if (x < x0) x0 = x;
    if (x > x1) x1 = x;
    if (y < y0) y0 = y;
    if (y > y1) y1 = y;
  }
  // Si un brazo iba a pasar el borde, se corre toda.
  cx += Math.max(0, MANCHA_AIRE - (cx + x0)) - Math.max(0, cx + x1 - (ancho - MANCHA_AIRE));
  cy += Math.max(0, MANCHA_AIRE - (cy + y0)) - Math.max(0, cy + y1 - (alto - MANCHA_AIRE));
  for (let i = 0; i < N; i++) {
    p[i * 2] += cx;
    p[i * 2 + 1] += cy;
  }
  // La normal (hacia afuera) y la luz de cada punto, por sus vecinos.
  const n = m.normales;
  for (let i = 0; i < N; i++) {
    const a = ((i + N - 1) % N) * 2;
    const b = ((i + 1) % N) * 2;
    const tx = p[b] - p[a];
    const ty = p[b + 1] - p[a + 1];
    const d = Math.hypot(tx, ty) || 1;
    n[i * 2] = ty / d;
    n[i * 2 + 1] = -tx / d;
    m.luz[i] = n[i * 2] * LUZ[0] + n[i * 2 + 1] * LUZ[1];
  }
  m.x = cx;
  m.y = cy;
  m.R = R;
  m.t = forma;

  // Los pedazos: salen hasta PEDAZO_LEJOS radios suyos más allá del borde y
  // vuelven a quedar adentro (ahí no se ven), siempre unidos por el cuello.
  // Sin lugar para alejarse —cerca del borde de la pantalla—, se quedan más
  // cerca.
  for (const pz of m.pedazos) {
    const ang = pz.angulo + pz.mece * Math.sin(forma * 0.06 + pz.vaiven);
    const r1 = R * factorMancha(m, ang, forma);
    const r2 = R * pz.tam;
    const k = 0.5 - 0.5 * Math.cos(pz.ritmo * forma + pz.fase);
    const dx = Math.cos(ang);
    const dy = Math.sin(ang);
    const margen = r2 + MANCHA_AIRE;
    const lugar = Math.min(
      dx > 1e-6 ? (ancho - margen - cx) / dx : dx < -1e-6 ? (margen - cx) / dx : Infinity,
      dy > 1e-6 ? (alto - margen - cy) / dy : dy < -1e-6 ? (margen - cy) / dy : Infinity,
    );
    const d = Math.max(0, Math.min(r1 + r2 * (-1.4 + (PEDAZO_LEJOS + 1.4) * k), lugar));
    pz.ang = ang;
    pz.r = r2;
    pz.d = d;
    pz.x = cx + dx * d;
    pz.y = cy + dy * d;
    // Del todo adentro de la mancha no se dibuja.
    pz.fuera = d + r2 > r1 + 0.5;
    cuelloPedazo(m, pz, forma);
  }
  return m;
}

/* Un contorno cerrado y sin quiebres por los puntos de `q` (curvas por los
   puntos medios, como el cuerpo de los zarcillos). Con `saltear`, los puntos
   para los que da `true` no se trazan y el contorno queda cortado ahí. */
function curvaCerrada(g, q, N, saltear = null) {
  const X = (i) => q[((i % N) + N) % N * 2];
  const Y = (i) => q[((i % N) + N) % N * 2 + 1];
  let desde = 0;
  if (saltear) {
    // Arranca justo después de un tramo salteado, si hay alguno.
    while (desde < N && !saltear(desde)) desde++;
    if (desde === N) saltear = null;
    else {
      while (desde < 2 * N && saltear(desde % N)) desde++;
      if (desde === 2 * N) return;
    }
  }
  if (!saltear) desde = 0;
  let suelto = true;
  for (let j = 0; j < N; j++) {
    const i = desde + j;
    if (saltear && saltear(i % N)) {
      suelto = true;
      continue;
    }
    if (suelto) g.moveTo((X(i - 1) + X(i)) / 2, (Y(i - 1) + Y(i)) / 2);
    suelto = false;
    g.quadraticCurveTo(X(i), Y(i), (X(i) + X(i + 1)) / 2, (Y(i) + Y(i + 1)) / 2);
  }
  if (!saltear) g.closePath();
}

/* El borde metido hacia adentro, en cada punto lo que diga `cuanto(i)`. */
function bordeAdentro(m, cuanto) {
  const N = PUNTOS_MANCHA;
  const q = m.aux;
  for (let i = 0; i < N; i++) {
    const d = cuanto(i);
    q[i * 2] = m.puntos[i * 2] - m.normales[i * 2] * d;
    q[i * 2 + 1] = m.puntos[i * 2 + 1] - m.normales[i * 2 + 1] * d;
  }
  return q;
}

/* La silueta de un pedazo: con cuello, de la mancha al pedazo por una curva,
   la vuelta por el lado de afuera del pedazo y de vuelta a la mancha por la
   otra curva; suelto, un círculo. `cerrar` lo cierra por adentro de la
   mancha, para el relleno; para el filo queda abierto. Se cierra pasando por
   el centro y no derecho de un agarre al otro: si entre los dos el borde de
   la mancha tiene un pliegue hacia adentro, la recta pasa por afuera de la
   mancha y entre las dos quedaba una cuña sin pintar, por donde se veía el
   resplandor. Todos giran para el mismo lado (el arco va al revés de las
   agujas), así que dos pedazos encimados no se restan en el relleno. */
function siluetaPedazo(g, pz, cerrar, m) {
  if (!pz.cuello) {
    g.moveTo(pz.x + pz.r, pz.y);
    g.arc(pz.x, pz.y, pz.r, Math.PI * 2, 0, true);
    return;
  }
  const p = pz.p;
  g.moveTo(p[0], p[1]);
  g.bezierCurveTo(p[8], p[9], p[12], p[13], p[4], p[5]);
  g.arc(pz.x, pz.y, pz.r, pz.a3, pz.a4, true);
  g.bezierCurveTo(p[14], p[15], p[10], p[11], p[2], p[3]);
  if (cerrar) {
    g.lineTo(m.x, m.y);
    g.closePath();
  }
}

/* Dibuja la mancha (ya puesta en su instante con `formaMancha`): el
   resplandor y la masa con sus pedazos, el lustre, la raya hueso, el filo
   con su rebote y las venas. */
export function dibujarMancha(g, m, tinta = TINTA_SIMBIONTE) {
  if (!m.R) return;
  const N = PUNTOS_MANCHA;
  const R = m.R;
  const pedazos = m.pedazos.filter((pz) => pz.fuera);
  // El resplandor: el contorno de todo trazado ancho y tenue ANTES del
  // relleno, que tapa la mitad de adentro. Es el halo de los zarcillos en
  // dos anchos, que se suman: se apaga de a poco sin desenfocar nada.
  g.lineJoin = "round";
  g.lineCap = "round";
  g.beginPath();
  curvaCerrada(g, m.puntos, N);
  for (const pz of pedazos) siluetaPedazo(g, pz, true, m);
  g.strokeStyle = tinta.halo;
  g.globalAlpha = 0.42;
  // En una mancha chica (un celular) el mismo ancho se leía como un borde
  // grueso alrededor, no como un resplandor: va en proporción, hasta un
  // poco más de la mitad.
  const proporcion = Math.min(1, Math.max(0.55, R / 210));
  for (const ancho of RESPLANDOR) {
    g.lineWidth = ancho * proporcion;
    g.stroke();
  }
  // La masa. Los pedazos en un relleno aparte: el cierre de su cuello pasa
  // por adentro de la mancha y, en el mismo relleno, según para dónde gire,
  // podría abrirle un hueco.
  g.globalAlpha = 0.96;
  g.fillStyle = tinta.goo;
  g.beginPath();
  curvaCerrada(g, m.puntos, N);
  g.fill();
  if (pedazos.length) {
    g.beginPath();
    for (const pz of pedazos) siluetaPedazo(g, pz, true, m);
    g.fill();
  }

  // El lustre y el rebote, en un solo degradado pintado con la forma de la
  // mancha (así queda adentro). Nace afuera, del lado de la luz, y se apaga
  // antes del medio: eso es el lustre. Y lejos de ahí, a casi dos radios, se
  // vuelve a prender tenue: hasta allá sólo llega el lado de la sombra, y es
  // la luz que rebota en lo mojado. Hace falta porque el lado de la luz es el
  // de arriba, y arriba suelen estar el título y las tablas: muchas veces lo
  // que se ve de la mancha es la mitad de abajo, y sin el rebote era una
  // sombra con un borde. (Eran dos rellenos; en uno cuesta la mitad.)
  const [lx, ly] = LUZ;
  const fx = m.x + lx * R * 0.85;
  const fy = m.y + ly * R * 0.85;
  const brillo = g.createRadialGradient(fx, fy, 0, fx, fy, R * 2.1);
  brillo.addColorStop(0, "rgba(130,138,240,0.3)");
  brillo.addColorStop(0.22, "rgba(130,138,240,0.1)");
  brillo.addColorStop(0.5, "rgba(130,138,240,0)");
  brillo.addColorStop(0.66, "rgba(150,140,255,0)");
  brillo.addColorStop(0.9, "rgba(150,140,255,0.15)");
  brillo.addColorStop(1, "rgba(150,140,255,0.15)");
  g.globalAlpha = 1;
  g.fillStyle = brillo;
  g.beginPath();
  curvaCerrada(g, m.puntos, N);
  g.fill();

  // La raya hueso: fina, que se afina en las puntas, sólo donde el borde
  // mira de lleno a la luz Y está del lado de la luz de la mancha: el costado
  // de un brazo que cuelga también puede mirar hacia arriba a la izquierda, y
  // ahí habría salido una segunda raya, abajo. Lo blanco lleno es uno solo y
  // arriba. Un contorno de ida y otro de vuelta un poco más adentro: entre
  // los dos, la raya (lo de afuera de la franja se resta).
  g.fillStyle = tinta.blanco;
  g.beginPath();
  curvaCerrada(g, bordeAdentro(m, () => R * 0.06), N);
  const q = m.aux;
  const raya = bordeAdentro(m, (i) => {
    const haciaLuz = ((m.puntos[i * 2] - m.x) * lx + (m.puntos[i * 2 + 1] - m.y) * ly) / R;
    const k = Math.max(0, (m.luz[i] - 0.5) / 0.5) * acotar((haciaLuz - 0.3) / 0.3, 0, 1);
    return R * (0.06 + 0.045 * Math.pow(k, 1.5));
  });
  // El de vuelta, al revés: se invierte el orden de los puntos en el lugar.
  for (let i = 1, j = N - 1; i < j; i++, j--) {
    const xi = q[i * 2];
    const yi = q[i * 2 + 1];
    q[i * 2] = q[j * 2];
    q[i * 2 + 1] = q[j * 2 + 1];
    q[j * 2] = xi;
    q[j * 2 + 1] = yi;
  }
  curvaCerrada(g, raya, N);
  g.fill();

  // El filo y el rebote en un solo trazo, con un degradado a lo largo de la
  // luz: frío y fuerte del lado de la luz, nada en los costados y un violeta
  // tenue del lado de la sombra. Donde se agarra un pedazo el borde no se
  // traza: ahí la mancha sigue en el cuello, no termina.
  const luz = g.createLinearGradient(m.x - lx * R * 1.2, m.y - ly * R * 1.2, m.x + lx * R * 1.2, m.y + ly * R * 1.2);
  luz.addColorStop(0, tinta.rebote);
  luz.addColorStop(0.36, "rgba(150,140,255,0)");
  luz.addColorStop(0.6, "rgba(168,178,255,0)");
  luz.addColorStop(1, tinta.filo);
  g.strokeStyle = luz;
  g.lineWidth = 1.6;
  g.beginPath();
  const agarres = pedazos.filter((pz) => pz.cuello);
  const paso = (Math.PI * 2) / N;
  curvaCerrada(
    g,
    bordeAdentro(m, () => 1),
    N,
    agarres.length
      ? (i) =>
          agarres.some((pz) => {
            const medio = (pz.agarre[0] + pz.agarre[1]) / 2;
            return Math.abs(giroEntre(i * paso, medio)) < Math.abs(pz.agarre[1] - medio) + paso;
          })
      : null,
  );
  for (const pz of pedazos) siluetaPedazo(g, pz, false);
  g.stroke();

  // Las venas: siguen a la mancha —van a una fracción del radio en cada
  // ángulo—, así que se estiran con los brazos. Cada una con una rama. Van
  // dos veces: ancha y violeta abajo, fina y hueso arriba; sola, la línea
  // fina se leía como la rajadura de una piedra.
  g.beginPath();
  const T = 10;
  for (const v of m.venas) {
    const punto = (u, extra = 0) => {
      const ang = v.desde + v.abarca * u;
      const f = (v.de + (v.a - v.de) * u + 0.035 * Math.sin(u * 4 + m.t * 0.3 + v.fase) + extra) * factorMancha(m, ang, m.t);
      return [m.x + Math.cos(ang) * R * f, m.y + Math.sin(ang) * R * f];
    };
    // Curvas por los puntos medios: con rectas, quebrada, se leía como la
    // rajadura de una piedra.
    let [ax, ay] = punto(0);
    g.moveTo(ax, ay);
    for (let j = 1; j <= T; j++) {
      const [x, y] = punto(j / T);
      if (j === T) g.quadraticCurveTo(ax, ay, x, y);
      else g.quadraticCurveTo(ax, ay, (ax + x) / 2, (ay + y) / 2);
      [ax, ay] = [x, y];
    }
    // La rama: sale de un poco más allá de la mitad, hacia el borde.
    const [rx, ry] = punto(0.55);
    const [kx, ky] = punto(0.62, 0.08);
    const [ex, ey] = punto(0.7, 0.16);
    g.moveTo(rx, ry);
    g.quadraticCurveTo(kx, ky, ex, ey);
  }
  g.strokeStyle = tinta.lustre;
  g.lineWidth = 4.5;
  g.globalAlpha = 0.7;
  g.stroke();
  g.strokeStyle = tinta.blanco;
  g.lineWidth = 1.1;
  g.globalAlpha = 0.5;
  g.stroke();
  g.globalAlpha = 1;
}

/* La caja de la mancha en el instante en que está: el borde, los pedazos y
   sus cuellos (las curvas no salen de sus puntos de control), más lo que
   abre el resplandor. En píxeles enteros. Y en `dentro`, [x0, y0, x1, y1],
   lo que ocupan de verdad el borde y los pedazos, sin el aire ni los puntos
   de control: es lo que `acomodarSello` cuida que no se salga. */
export function cajaMancha(m) {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  const ver = (x, y, r = 0) => {
    if (x - r < x0) x0 = x - r;
    if (y - r < y0) y0 = y - r;
    if (x + r > x1) x1 = x + r;
    if (y + r > y1) y1 = y + r;
  };
  for (let i = 0; i < m.puntos.length; i += 2) ver(m.puntos[i], m.puntos[i + 1]);
  const afuera = m.pedazos.filter((pz) => pz.fuera);
  for (const pz of afuera) ver(pz.x, pz.y, pz.r);
  const dentro = [x0, y0, x1, y1];
  for (const pz of afuera) if (pz.cuello) for (let i = 0; i < 16; i += 2) ver(pz.p[i], pz.p[i + 1]);
  const aire = RESPLANDOR[0] / 2 + 2;
  const x = Math.floor(x0 - aire);
  const y = Math.floor(y0 - aire);
  return { x, y, ancho: Math.ceil(x1 + aire) - x, alto: Math.ceil(y1 + aire) - y, dentro };
}

/* LA MANCHA ESTAMPADA. Dibujarla entera en cada cuadro es mucha superficie
   para pintar: medido en Chromium a 1440×900 con la CPU frenada cuatro veces
   (lo que se parece a una PC de oficina sin placa de video), el ambiente de
   Venom bajaba de 60 a 30 cuadros por segundo, y no por la cuenta (2 ms) sino
   por lo que hay que pintar —el resplandor, los brillos, el relleno—. Así que
   se dibuja en un lienzo aparte (`sello`) sólo cuando `rehacer` lo pide
   —quien la mueve lo pide uno de cada tres cuadros: cambia de forma tan
   despacio que, a veinte veces por segundo, ningún punto salta ni un píxel;
   en una PC lenta, menos seguido— y en cada cuadro se estampa ese dibujo
   donde está el centro AHORA. Así el paseo sigue liso aunque la forma se
   rehaga de a saltitos. El estampado cae en píxeles enteros de la pantalla:
   corrido medio píxel, el dibujo se interpola y los filos finos titilan. */
export function estamparMancha(g, m, sello, escala, rehacer, alfa = 1) {
  if (rehacer || !sello.caja) {
    const caja = cajaMancha(m);
    const w = Math.ceil(caja.ancho * escala);
    const h = Math.ceil(caja.alto * escala);
    const l = sello.lienzo;
    // Sólo crece: achicarlo a cada rato es pedir memoria nueva cada vez.
    if (l.width < w || l.height < h) {
      l.width = Math.max(l.width, w);
      l.height = Math.max(l.height, h);
    }
    const sg = sello.g;
    sg.setTransform(1, 0, 0, 1, 0, 0);
    sg.clearRect(0, 0, Math.max(w, sello.w || 0), Math.max(h, sello.h || 0));
    sg.setTransform(escala, 0, 0, escala, -caja.x * escala, -caja.y * escala);
    dibujarMancha(sg, m);
    Object.assign(sello, { caja, w, h, cx: m.x, cy: m.y });
  }
  const dx = Math.round((sello.caja.x + m.x - sello.cx) * escala);
  const dy = Math.round((sello.caja.y + m.y - sello.cy) * escala);
  g.setTransform(1, 0, 0, 1, 0, 0);
  // `alfa` es para cuando se va (ver `seVaMancha` en `crearAmbiente`).
  g.globalAlpha = alfa;
  g.drawImage(sello.lienzo, 0, 0, sello.w, sello.h, dx, dy, sello.w, sello.h);
  g.globalAlpha = 1;
  g.setTransform(escala, 0, 0, escala, 0, 0);
}

/* Con la forma quieta (una PC que no da abasto, ver «EL PISO» en
   `crearAmbiente`) se estampa siempre el mismo dibujo, y sus pedazos ya no se
   acercan a la mancha cuando el paseo la lleva contra el borde: un pedazo
   que en el dibujo estaba afuera quedaría cortado por el borde de la
   pantalla. Así que el centro se corre lo que haga falta para que lo que
   ocupan el borde y los pedazos (`dentro` de la caja) quede a MANCHA_AIRE
   de los bordes, como cuando se mueve. Al quedarse quieta ya estaba así,
   así que no salta. */
export function acomodarSello(m, sello, ancho, alto) {
  const { caja } = sello;
  if (!caja) return;
  const [x0, y0, x1, y1] = caja.dentro;
  m.x = acotar(m.x, MANCHA_AIRE + sello.cx - x0, ancho - MANCHA_AIRE - (x1 - sello.cx));
  m.y = acotar(m.y, MANCHA_AIRE + sello.cy - y0, alto - MANCHA_AIRE - (y1 - sello.cy));
}

/* El lienzo aparte de la mancha, si el navegador lo tiene. Si no, la mancha
   se dibuja directo en cada cuadro: cuesta más, pero se ve igual. */
function selloMancha() {
  if (typeof OffscreenCanvas === "undefined") return null;
  try {
    const lienzo = new OffscreenCanvas(1, 1);
    const g = lienzo.getContext("2d");
    return g ? { lienzo, g } : null;
  } catch {
    return null;
  }
}

/* ── La lluvia (Batman) ────────────────────────────────────────────────────────
 * Guillermo pidió Batman, «Dark Knight», y lo que se quería era un ambiente
 * SOBRIO: si algo distrae en una pantalla de trabajo, va quieto. Es lluvia de
 * noche que cae despacio —más despacio que la de verdad, que a esa velocidad
 * ya no se lee como lluvia sino como rayas—: trazos finos y tenues, apenas
 * inclinados por el viento, en tres distancias. Las de atrás son cortas,
 * lentas y casi no se ven; las de adelante, más largas y más rápidas. Sin
 * relámpagos: un fogonazo cada tanto es exactamente lo que distrae.
 *
 * Se dibujan todas juntas: un solo trazo por distancia y por cuadro (tres en
 * total, haya las gotas que haya), que es lo más barato que se le puede
 * pedir a un lienzo. Va detrás del contenido, como todo ambiente, y con
 * «menos movimiento» no arranca: queda la batiseñal quieta del fondo. */
export const CAPAS_LLUVIA = 3;
export const LLUVIA = {
  largo: [7, 22],
  // Píxeles por segundo.
  vel: [150, 330],
  alfa: [0.1, 0.3],
  // Lo que se corre de costado por cada píxel que baja: hacia la izquierda.
  viento: -0.16,
};

/* Una gota nueva. `arriba` la hace nacer por encima de la pantalla, que es
   como entra la que reemplaza a una que salió por abajo; las del arranque
   nacen repartidas. Como el viento las corre a la izquierda, nacen también
   un poco a la derecha del borde: si no, ese costado quedaría seco. */
export function nuevaGotaLluvia(ancho, alto, azar = Math.random, arriba = false) {
  const z = azar();
  const largo = entre(LLUVIA.largo, z);
  return {
    z,
    capa: Math.min(CAPAS_LLUVIA - 1, Math.floor(z * CAPAS_LLUVIA)),
    largo,
    x: azar() * (ancho - LLUVIA.viento * alto * 0.5),
    y: arriba ? -largo - azar() * alto * 0.3 : azar() * alto,
    vel: entre(LLUVIA.vel, z) * (0.9 + azar() * 0.2),
  };
}

/* Un paso: baja, y el viento la corre un poco. Devuelve `true` si ya salió
   por abajo y hay que reemplazarla. La que el viento saca por un costado
   vuelve por el otro. */
export function moverLluvia(gt, dt, ancho, alto) {
  const baja = gt.vel * dt;
  gt.y += baja;
  gt.x += baja * LLUVIA.viento;
  if (gt.x < -gt.largo) gt.x += ancho + gt.largo * 2;
  return gt.y - gt.largo > alto;
}

/* Todas las gotas, de atrás para adelante: un trazo por distancia. Sobre un
   fondo oscuro, gris azulado claro; sobre uno claro, tinta. */
export function dibujarLluvia(g, gotas, sobreClaro = false) {
  const tinta = sobreClaro ? "36,42,56" : "196,206,224";
  g.lineCap = "round";
  for (let c = 0; c < CAPAS_LLUVIA; c++) {
    let alguna = false;
    g.beginPath();
    for (const gt of gotas) {
      if (gt.capa !== c) continue;
      alguna = true;
      g.moveTo(gt.x, gt.y);
      g.lineTo(gt.x - gt.largo * LLUVIA.viento, gt.y - gt.largo);
    }
    if (!alguna) continue;
    g.strokeStyle = `rgba(${tinta},${entre(LLUVIA.alfa, (c + 0.5) / CAPAS_LLUVIA).toFixed(3)})`;
    g.lineWidth = 0.8 + c * 0.35;
    g.stroke();
  }
}

/* ── Las trenzas ─────────────────────────────────────────────────────────────
 *
 * Lautaro (Satelital, 13/10), por mail a Cepi, después de ver la cara de
 * Venom de fondo: «no está fea, pero prefiero tener un fondo más como el que
 * tenía antes, pero aún con más tentáculos enredándose y más Venom sin su
 * cara». Volvió el fondo de antes (la mancha, los zarcillos, las gotas) y se
 * le suman las trenzas: tentáculos enredados entre sí. Unas asoman por los
 * bordes, como los zarcillos; dos le salen a la mancha grande, que así deja
 * de ser una sombra que pasea y estira los brazos. Sin cara, como todo lo
 * demás: es goo, cualquiera, dibujado por código.
 *
 * Son de dos clases, mitad y mitad:
 *
 *   Trenzadas    dos o tres tentáculos del mismo grosor, enroscados uno en
 *                otro.
 *   Enroscadas   un tentáculo grueso (el tronco) y uno o dos finitos que le
 *                dan vueltas alrededor, pegados a él.
 *
 * ── QUÉ LAS HACE ENREDADAS Y NO CRUZADAS ────────────────────────────────────
 *
 *   El arriba y abajo  dos líneas negras que se cruzan son una X; enredadas,
 *                      cada una pasa por encima de la otra y después por
 *                      debajo. Cada hebra es una hélice vista de costado: se
 *                      corre a un lado y al otro del eje (el seno) y va
 *                      adelante y atrás (el coseno). Se dibuja primero lo de
 *                      atrás de todas las hebras, después el tronco si tiene,
 *                      y al final lo de adelante. Dos hebras se cruzan justo
 *                      donde una está adelante y la otra atrás (y una que
 *                      cruza el tronco, bien adelante o bien atrás), así que
 *                      cada cruce queda bien sin ordenar nada a mano.
 *   El corte           lo de adelante (y el tronco) lleva primero su cinta
 *                      tres px más ancha de cada lado, maciza, en el halo frío
 *                      ya mezclado con el negro: tapa el filo de lo que pasa
 *                      por debajo justo al lado del cruce, y queda como el
 *                      halo de lo de arriba. Es lo que dice «ésta va por
 *                      encima».
 *   La profundidad     lo de atrás lleva el halo tenue y transparente y el
 *                      filo a media luz; lo de adelante, el filo entero y una
 *                      raya blanca corta en el lomo.
 *   El enroscarse      la vuelta corre a lo largo despacio (unos pocos px por
 *                      segundo): los cruces avanzan, como algo que se sigue
 *                      enroscando. Y no es pareja: se aprieta y se afloja a lo
 *                      largo, que una trenza pareja es una soga.
 *   Las puntas         juntas en la raíz, se afinan y se abren hacia la
 *                      punta: cada una busca por su lado.
 *
 * CUESTAN POCO, pero son lo que más cuesta del ambiente. Siete pasadas por
 * trenza y por cuadro (tres lo de atrás y cuatro lo de adelante; el tronco,
 * cuatro más), cada una con todas sus hebras juntas en un mismo camino. Casi
 * todo es relleno, con rectas: medido en Chromium pintando con el procesador
 * y la CPU frenada cuatro veces, con el halo hecho con un trazo ancho y los
 * bordes con curvas la página andaba a 18 cuadros por segundo; con las cintas
 * rellenas, que se ven igual, a 30. Sin `filter`, sin
 * `shadowBlur`, sin imágenes. Dos hebras que se tocan en el mismo relleno no
 * se restan: todas se trazan con el mismo giro (un borde de ida y el otro de
 * vuelta). Igual una trenza cuesta como dos zarcillos, así que tienen su piso
 * (ver «EL PISO DE LAS TRENZAS» en `crearAmbiente`): en una PC que no da
 * abasto se apagan, más cuanto peor anda, y vuelven de a una cuando anda; y
 * si la mancha se fue, se van todas y esa PC queda como estaba antes. */

// Cuántos tramos tiene cada hebra: con menos, la onda de la vuelta se quiebra.
const TRAMOS_TRENZA = 32;
// El «borde» de las que le salen a la mancha.
export const DE_LA_MANCHA = 4;
// Para dónde le salen a la mancha: a los costados o abajo, nunca para
// arriba (arriba están el título y las tablas, como con los pedazos).
export const SECTORES_MANCHA = [
  [-0.3, 0.55],
  [1.15, 2.0],
  [Math.PI - 0.55, Math.PI + 0.3],
];
// Y lo más para arriba que apuntan, de la raíz a la punta: lo mismo que el
// borde de los sectores, 0,3 radianes (17°) sobre la horizontal. Con el
// desvío, la curva y la onda, la punta llegaba a subir más de 200 px y a
// pasar por encima de la mancha (ver `espinaTrenza`).
export const SUBE_MANCHA = Math.sin(0.3);

const suave = (v) => {
  const k = Math.min(1, Math.max(0, v));
  return k * k * (3 - 2 * k);
};

/* Lo que tienen todas, salgan de donde salgan. Pide siempre los mismos
   números al azar, en el mismo orden, sea de la clase que sea. */
function trenzaBase(z, grosor, azar) {
  const tronco = azar() < 0.5;
  const k = azar();
  const hebras = tronco ? (k < 0.5 ? 1 : 2) : k < 0.55 ? 2 : 3;
  return {
    z,
    grosor,
    tronco,
    hebras,
    // Cuántos px de largo lleva una vuelta entera, y cuánto se aprieta y se
    // afloja a lo largo.
    paso: grosor * (6 + azar() * 3),
    aprieta: 0.35 + azar() * 0.35,
    apriFase: azar() * Math.PI * 2,
    // Para qué lado se enrosca y a qué ritmo (radianes por segundo).
    giro: (azar() < 0.5 ? -1 : 1) * (0.45 + azar() * 0.45),
    giroFase: azar() * Math.PI * 2,
    // Cuánto se corre cada hebra del eje, en grosores: tres necesitan más
    // lugar que dos para que se vea que son tres. (Las que se enroscan en un
    // tronco van pegadas a él: eso sale de los anchos, en `espinaTrenza`.)
    corre: hebras === 3 ? 0.74 : 0.62,
    abre: 0.5 + azar() * 0.7,
    // El eje: menos ondas y más lentas que un zarcillo.
    ondas: [0.35 + azar() * 0.5, 0.3 + azar() * 0.4, azar() * Math.PI * 2],
    curva: (azar() - 0.5) * 1.2,
    vel: entre([16, 26], z) * (0.85 + azar() * 0.3),
    semilla: Math.floor(azar() * 1e4),
    largo: 0,
    fase: "asoma",
    espera: 0,
    eje: null,
    hilos: null,
  };
}

/* Escondida, a medio salir o ya afuera, como los zarcillos. */
function arrancarTrenza(tr, k, entra) {
  if (entra) Object.assign(tr, { fase: "oculto", espera: 0.5 + k * 2.5 });
  else if (k < 0.5) tr.largo = k * 2 * tr.largoMax;
  else Object.assign(tr, { fase: "queda", largo: tr.largoMax, espera: 2 + (k - 0.5) * 16 });
  return tr;
}

/* Una trenza que asoma por un borde de la pantalla. Más larga que un
   zarcillo —necesita largo para que se vean dos o tres cruces—, pero tampoco
   llega a la mitad. */
export function nuevaTrenza(ancho, alto, azar = Math.random, entra = false) {
  const z = azar();
  const borde = bordeDe(azar());
  const corto = Math.max(1, Math.min(ancho, alto));
  const tr = trenzaBase(z, entre([17, 27], z), azar);
  Object.assign(tr, {
    borde,
    donde: 0.1 + azar() * 0.8,
    desvio: (azar() - 0.5) * 0.8,
    largoMax: corto * entre([0.22, 0.36], z) + 40 + (borde === 2 ? 50 : 0),
  });
  anclar(tr, ancho, alto);
  return arrancarTrenza(tr, azar(), entra);
}

/* Una trenza que le sale a la mancha. El tamaño va con el de la mancha (en
   un celular, todo más chico). `otras` son las que ya le salen: ésta va por
   otro lado, a más de un radián de cada una. */
export function nuevaTrenzaDeLaMancha(ancho, alto, azar = Math.random, entra = false, otras = []) {
  const R = radioMancha(ancho, alto);
  const z = azar();
  const tr = trenzaBase(z, acotar(R * 0.12, 10, 26), azar);
  const lejos = (a) => otras.every((o) => Math.abs(giroEntre(a, o.ang)) > 1);
  const s = Math.floor(azar() * SECTORES_MANCHA.length) % SECTORES_MANCHA.length;
  const u = azar();
  let ang = 0;
  for (let i = 0; i < SECTORES_MANCHA.length; i++) {
    const [a, b] = SECTORES_MANCHA[(s + i) % SECTORES_MANCHA.length];
    ang = a + (b - a) * u;
    if (lejos(ang)) break;
    // En el mismo sector, del otro extremo.
    ang = a + (b - a) * (1 - u);
    if (lejos(ang)) break;
  }
  Object.assign(tr, {
    borde: DE_LA_MANCHA,
    ang,
    desvio: (azar() - 0.5) * 0.5,
    // La raíz está metida en la mancha, a un quinto de su borde (ver
    // `anclarTrenza`): lo que asoma mide de tres cuartos a una vez y media
    // el radio.
    largoMax: R * (1.1 + z * 0.55),
  });
  return arrancarTrenza(tr, azar(), entra);
}

/* Dónde está la raíz y hacia dónde mira. Las de la mancha nacen adentro de
   ella, a un poco más de tres cuartos del radio que tiene hacia ese lado (si
   ahí estira un brazo, la raíz va con el brazo), y miran hacia afuera.
   Devuelve `false` si es de la mancha y la mancha no está. */
function anclarTrenza(tr, ancho, alto, m) {
  if (tr.borde !== DE_LA_MANCHA) {
    anclar(tr, ancho, alto);
    return true;
  }
  if (!m || !m.R) return false;
  const r = m.R * factorMancha(m, tr.ang, m.t) * 0.78;
  tr.x = m.x + Math.cos(tr.ang) * r;
  tr.y = m.y + Math.sin(tr.ang) * r;
  tr.rumbo = tr.ang + tr.desvio;
  return true;
}

/* Las hebras en el instante `t`: el eje (de la raíz a la punta, como el
   espinazo de un zarcillo) y, por hebra, sus puntos, sus normales, su ancho
   y si en cada punto va adelante (`hondo` positivo) o atrás. Si tiene
   tronco, es la primera, y va por el eje. */
export function espinaTrenza(tr, t) {
  const N = TRAMOS_TRENZA;
  const respira = 1 + 0.035 * Math.sin(t * 0.8 + tr.ondas[2]);
  const L = tr.largo * respira;
  const ds = L / N;
  const eje = tr.eje || (tr.eje = new Float64Array((N + 1) * 2));
  const [cuantas, ritmo, fase] = tr.ondas;
  // Las de la mancha, nunca más para arriba que `SUBE_MANCHA`: donde el eje
  // apuntaría más arriba, va a ese ángulo justo (para el lado que iba).
  const sube = tr.borde === DE_LA_MANCHA ? SUBE_MANCHA : 1;
  const acostado = Math.sqrt(1 - sube * sube);
  let x = tr.x;
  let y = tr.y;
  for (let i = 0; i <= N; i++) {
    const u = i / N;
    eje[i * 2] = x;
    eje[i * 2 + 1] = y;
    const ang = tr.rumbo + tr.curva * u * u + Math.sin(u * cuantas * Math.PI * 2 - t * ritmo + fase) * 0.3 * u;
    let dx = Math.cos(ang);
    let dy = Math.sin(ang);
    if (dy < -sube) {
      dy = -sube;
      dx = dx < 0 ? -acostado : acostado;
    }
    x += dx * ds;
    y += dy * ds;
  }
  const cuantosHilos = tr.hebras + (tr.tronco ? 1 : 0);
  if (!tr.hilos)
    tr.hilos = Array.from({ length: cuantosHilos }, () => ({
      p: new Float64Array((N + 1) * 2),
      n: new Float64Array((N + 1) * 2),
      an: new Float64Array(N + 1),
      hondo: new Float64Array(N + 1),
    }));
  // Recién asomada es corta y finita, no un manojo grueso.
  const crece = 0.45 + 0.55 * Math.min(1, L / (tr.largoMax * 0.5));
  // El ancho a lo largo: grueso en la raíz y en punta al final, con bultos
  // que corren (está vivo). El tronco es más grueso; lo que se le enrosca,
  // más fino.
  const ancho = (u, cuanto, desfase) =>
    tr.grosor * cuanto * (0.12 + 0.88 * Math.pow(1 - u, 0.7)) * (1 + 0.12 * Math.sin(u * 8 - t * 1.2 + desfase)) * crece;
  for (let j = 0; j < cuantosHilos; j++) {
    const hilo = tr.hilos[j];
    const esTronco = tr.tronco && j === 0;
    const h = tr.tronco ? j - 1 : j;
    const desfase = (h * Math.PI * 2) / tr.hebras;
    for (let i = 0; i <= N; i++) {
      const u = i / N;
      // La normal del eje, por sus vecinos.
      const a = Math.max(0, i - 1) * 2;
      const b = Math.min(N, i + 1) * 2;
      const dx = eje[b] - eje[a];
      const dy = eje[b + 1] - eje[a + 1];
      const d = Math.hypot(dx, dy) || 1;
      let corre = 0;
      if (esTronco) {
        hilo.an[i] = ancho(u, 1.25, 0);
        hilo.hondo[i] = 0;
      } else {
        // La vuelta: avanza siempre (el seno de lo que se aprieta nunca la
        // hace volver para atrás) y corre despacio con el tiempo.
        const s = (u * L) / tr.paso;
        const th =
          Math.PI * 2 * s + tr.aprieta * Math.sin((Math.PI * 2 * s) / 2.7 + tr.apriFase) - tr.giro * t + tr.giroFase + desfase;
        hilo.an[i] = ancho(u, tr.tronco ? 0.58 : 1, desfase);
        hilo.hondo[i] = Math.cos(th);
        // Pegada al tronco, o alrededor del eje; juntas en la raíz, y
        // abriéndose hacia la punta.
        const radio = tr.tronco ? (ancho(u, 1.25, 0) + hilo.an[i]) * 0.42 : tr.grosor * tr.corre;
        corre = radio * suave(u / 0.12) * (1 + tr.abre * suave((u - 0.65) / 0.35)) * Math.sin(th);
      }
      hilo.p[i * 2] = eje[i * 2] - (dy / d) * corre;
      hilo.p[i * 2 + 1] = eje[i * 2 + 1] + (dx / d) * corre;
    }
    // La normal de la hebra, que no es la del eje: va y viene alrededor.
    for (let i = 0; i <= N; i++) {
      const a = Math.max(0, i - 1) * 2;
      const b = Math.min(N, i + 1) * 2;
      const dx = hilo.p[b] - hilo.p[a];
      const dy = hilo.p[b + 1] - hilo.p[a + 1];
      const d = Math.hypot(dx, dy) || 1;
      hilo.n[i * 2] = -dy / d;
      hilo.n[i * 2 + 1] = dx / d;
    }
  }
  return tr;
}

/* Un paso de tiempo. Devuelve `true` cuando la trenza terminó de meterse (o
   era de la mancha y la mancha ya no está): hay que reemplazarla. Se queda
   afuera más que un zarcillo, y escondida menos: es lo que más se ve. */
export function moverTrenza(tr, dt, ancho, alto, t, m = null) {
  if (!anclarTrenza(tr, ancho, alto, m)) return true;
  if (tr.fase === "oculto") {
    tr.espera -= dt;
    if (tr.espera <= 0) tr.fase = "asoma";
  } else if (tr.fase === "asoma") {
    const k = tr.largo / tr.largoMax;
    tr.largo = Math.min(tr.largoMax, tr.largo + tr.vel * (1 - 0.55 * k) * dt);
    if (tr.largo >= tr.largoMax - 0.5) {
      tr.largo = tr.largoMax;
      tr.fase = "queda";
      tr.espera = 8 + suerte(tr.semilla) * 12;
    }
  } else if (tr.fase === "queda") {
    tr.espera -= dt;
    if (tr.espera <= 0) tr.fase = "vuelve";
  } else {
    tr.largo = Math.max(0, tr.largo - tr.vel * 1.3 * dt);
    if (tr.largo <= 0) return true;
  }
  espinaTrenza(tr, t);
  return false;
}

/* Los tramos de una hebra que van adelante (o atrás): pares [desde, hasta]
   de puntos seguidos del mismo lado. El tronco va entero en una sola. */
export function tramosDe(hilo, adelante, N = TRAMOS_TRENZA) {
  const out = [];
  let a = -1;
  for (let i = 0; i <= N; i++) {
    const va = (hilo.hondo[i] >= 0) === adelante;
    if (va && a < 0) a = i;
    if (!va && a >= 0) {
      out.push(a, i - 1);
      a = -1;
    }
  }
  if (a >= 0) out.push(a, N);
  return out;
}

/* Un borde de la hebra, de `a` a `b` (para cualquier lado), corrido del
   centro medio ancho más `extra` hacia `lado`. Rectas: con 32 tramos ya no
   se ve el quiebre, y una curva cuesta varias veces más de pintar en una PC
   sin placa de video (medido: ver «CUESTAN POCO» arriba). */
function bordeHebra(g, hilo, a, b, lado, extra, seguir) {
  const { p, n, an } = hilo;
  const paso = b >= a ? 1 : -1;
  for (let i = a; ; i += paso) {
    const d = lado * Math.max(0, an[i] / 2 + extra);
    const x = p[i * 2] + n[i * 2] * d;
    const y = p[i * 2 + 1] + n[i * 2 + 1] * d;
    if (i === a && !seguir) g.moveTo(x, y);
    else g.lineTo(x, y);
    if (i === b) break;
  }
}

/* La cinta de un tramo, `extra` más ancha de cada lado, cerrada: un borde de
   ida y el otro de vuelta (todas con el mismo giro, así el relleno las suma).
   Si llega a la punta, la punta es redonda. */
function cintaHebra(g, hilo, a, b, extra, N) {
  bordeHebra(g, hilo, a, b, 1, extra, false);
  if (b === N) {
    const { p, n, an } = hilo;
    const fx = p[N * 2] - p[(N - 1) * 2];
    const fy = p[N * 2 + 1] - p[(N - 1) * 2 + 1];
    const fd = Math.hypot(fx, fy) || 1;
    const r = an[N] / 2 + extra;
    g.quadraticCurveTo(
      p[N * 2] + (fx / fd) * r * 2.4,
      p[N * 2 + 1] + (fy / fd) * r * 2.4,
      p[N * 2] - n[N * 2] * r,
      p[N * 2 + 1] - n[N * 2 + 1] * r,
    );
  }
  bordeHebra(g, hilo, b, a, -1, extra, true);
  g.closePath();
}

/* Una pasada: los tramos de `hilos` que dice `tramos` (por hilo, pares
   [desde, hasta]). Son todos rellenos menos dos trazos finos:
     el halo     la cinta tres px más ancha de cada lado, en el frío del
                 reflejo, ANTES del cuerpo, que tapa lo de adentro. Atrás,
                 tenue y transparente; adelante (y el tronco), del mismo color
                 pero ya mezclado con el negro y macizo: eso es el corte, que
                 tapa el filo de lo que pasa por debajo al lado del cruce.
     el cuerpo   la cinta, en negro.
     el filo     un trazo fino del lado de la luz: entero adelante, a media
                 luz atrás.
     el brillo   sólo adelante: una raya blanca corta en el medio de cada
                 tramo, donde la hebra está más arriba. Una línea, nunca un
                 punto.
   Cada tramo llega hasta donde arranca el que sigue: ni se pisan ni queda un
   hueco. Donde cambia de lado la hebra está lo más lejos del eje (con tronco,
   en el borde del tronco), así que ahí no se pisa con otra hebra. */
function pasadaTrenza(g, tr, hilos, tramos, adelante, raiz, lado, tinta, alfa) {
  const N = TRAMOS_TRENZA;
  const cada = (minimo, hasta, hacer) => {
    for (let h = 0; h < hilos.length; h++) {
      const t = tramos[h];
      for (let q = 0; q < t.length; q += 2) {
        const a = t[q];
        const b = Math.min(N, t[q + 1] + (hasta ? 1 : 0));
        if (b - a >= minimo) hacer(hilos[h], a, b);
      }
    }
  };
  // La raíz de las de los bordes: dos bultos, como la de un zarcillo, del
  // mismo giro que las cintas (si no, el relleno los restaría).
  const k = Math.min(1, tr.largo / tr.largoMax);
  const r = tr.grosor * (0.9 + 0.6 * k);
  const bx = tr.x + Math.cos(tr.rumbo + Math.PI / 2) * r * 0.8;
  const by = tr.y + Math.sin(tr.rumbo + Math.PI / 2) * r * 0.8;
  const bultos = (extra) => {
    g.moveTo(tr.x + r + extra, tr.y);
    g.arc(tr.x, tr.y, r + extra, Math.PI * 2, 0, true);
    g.moveTo(bx + r * 0.7 + extra, by);
    g.arc(bx, by, r * 0.7 + extra, Math.PI * 2, 0, true);
  };
  let hay = raiz;
  cada(1, true, () => {
    hay = true;
  });
  if (!hay) return;

  g.beginPath();
  cada(1, true, (hilo, a, b) => cintaHebra(g, hilo, a, b, 3, N));
  if (raiz) bultos(3);
  g.globalAlpha = adelante ? alfa : alfa * 0.8;
  g.fillStyle = adelante ? tinta.corte : tinta.halo;
  g.fill();

  g.beginPath();
  cada(1, true, (hilo, a, b) => cintaHebra(g, hilo, a, b, 0, N));
  if (raiz) bultos(0);
  g.globalAlpha = alfa;
  g.fillStyle = tinta.goo;
  g.fill();

  g.beginPath();
  cada(1, true, (hilo, a, b) => bordeHebra(g, hilo, a, b, lado, -1, false));
  g.globalAlpha = alfa * (adelante ? 1 : 0.45);
  g.strokeStyle = tinta.filo;
  g.lineWidth = adelante ? 1.3 : 1;
  g.stroke();

  if (adelante) {
    g.beginPath();
    cada(3, false, (hilo, a, b) => {
      const medio = Math.round((a + b) / 2);
      bordeHebra(g, hilo, medio - 1, Math.min(N, medio + 1), lado, -0.28 * hilo.an[medio], false);
    });
    g.globalAlpha = alfa * 0.8;
    g.strokeStyle = tinta.blanco;
    g.lineWidth = 1;
    g.stroke();
  }
}

/* Dibuja una trenza: lo de atrás de sus hebras, el tronco si tiene, y lo de
   adelante. `alfa` es para las de la mancha cuando la mancha se va. */
export function dibujarTrenza(g, tr, tinta = TINTA_SIMBIONTE, alfa = 1) {
  if (tr.fase === "oculto" || tr.largo < 0.5 || !tr.hilos || alfa <= 0) return;
  const eje = tr.eje;
  // De qué lado le da la luz, por cómo sale (como en los zarcillos).
  const ex = eje[2] - eje[0];
  const ey = eje[3] - eje[1];
  const lado = -ey * LUZ[0] + ex * LUZ[1] > 0 ? 1 : -1;
  const raiz = tr.borde !== DE_LA_MANCHA;
  const hebras = tr.tronco ? tr.hilos.slice(1) : tr.hilos;
  g.lineCap = "round";
  g.lineJoin = "round";
  pasadaTrenza(g, tr, hebras, hebras.map((hilo) => tramosDe(hilo, false)), false, raiz, lado, tinta, alfa);
  if (tr.tronco) pasadaTrenza(g, tr, [tr.hilos[0]], [[0, TRAMOS_TRENZA]], true, false, lado, tinta, alfa);
  pasadaTrenza(g, tr, hebras, hebras.map((hilo) => tramosDe(hilo, true)), true, false, lado, tinta, alfa);
  g.globalAlpha = 1;
}

/* EL PISO DE LAS TRENZAS: la cuenta (ver «EL PISO DE LAS TRENZAS» en
   `crearAmbiente`). Se le pasa cada cuadro con el reloj, cuánto pasó de
   verdad desde el anterior (sin recortar; `null` si no se sabe, como en el
   primero) y cuántas trenzas hay; devuelve cuántas puede haber. Sin estado
   escondido: todo está en `piso`. */
// Cuadros por tanda, y cuántas tandas atrás se mira el cuadro más corto.
export const TANDA_TRENZAS = 60;
const TANDAS_RAPIDO = 4;
// Los segundos buenos que hacen falta para que vuelva una, al principio y
// como mucho (se duplican cada vez que una que volvió no se banca).
export const ESPERA_TRENZAS = 20;
export const ESPERA_MAX_TRENZAS = 160;
export const nuevoPisoTrenzas = () => ({
  cuadros: 0,
  tarde: 0,
  tiempo: 0,
  rapido: Infinity,
  rapidos: [],
  bien: 0,
  espera: ESPERA_TRENZAS,
  devolvio: false,
  cupo: Infinity,
});
export function contarCuadroTrenzas(piso, t, intervalo, vivas) {
  if (intervalo == null) return piso.cupo;
  // Lo más rápido que da esta pantalla: el cuadro más corto de las últimas
  // tandas (los de la carga también sirven para esto).
  if (intervalo > 0.003) piso.rapido = Math.min(piso.rapido, intervalo);
  if (t <= 4) return piso.cupo;
  piso.cuadros++;
  piso.tiempo += Math.min(intervalo, 0.1);
  const rapido = Math.min(piso.rapido, ...piso.rapidos);
  if (intervalo > Math.max(1 / 40, rapido * 1.4)) piso.tarde++;
  if (piso.cuadros < TANDA_TRENZAS) return piso.cupo;
  const hay = Math.min(piso.cupo, vivas);
  if (piso.tarde > TANDA_TRENZAS / 15) {
    // Sobran según cuántos llegaron tarde: con pocos, una; con uno de cada
    // cuatro, la mitad; con la mitad o más, todas.
    const sobran = Math.max(1, Math.round(hay * Math.min(1, (2 * piso.tarde) / TANDA_TRENZAS)));
    piso.cupo = Math.max(0, hay - sobran);
    // Si alguna ya había vuelto y otra vez no da, la próxima espera el doble.
    if (piso.devolvio) piso.espera = Math.min(ESPERA_MAX_TRENZAS, piso.espera * 2);
    piso.devolvio = false;
    piso.bien = 0;
  } else if (piso.tarde <= 1) {
    piso.bien += piso.tiempo;
    // Vuelve una sólo si el cupo es lo que las frena (si no, ya están todas).
    if (piso.cupo !== Infinity && vivas >= piso.cupo && piso.bien >= piso.espera) {
      piso.cupo++;
      piso.devolvio = true;
      piso.bien = 0;
    }
  } else piso.bien = 0;
  piso.rapidos.push(piso.rapido);
  if (piso.rapidos.length > TANDAS_RAPIDO) piso.rapidos.shift();
  piso.rapido = Infinity;
  piso.cuadros = 0;
  piso.tarde = 0;
  piso.tiempo = 0;
  return piso.cupo;
}

/* ── El motor ──────────────────────────────────────────────────────────────── */

const ESCENAS = {
  playa: dibujarPlaya,
  montana: dibujarMontana,
  desierto: dibujarDesierto,
  invasores: dibujarInvasores,
};

/* Cuántas van en una pantalla de `ancho`×`alto`: una grande lleva más y un
   celular menos, y ninguna pasa del `maximo` del tema si lo tiene. */
export function cuantasVan(conf, ancho, alto) {
  const cuantas = Math.round(
    conf.cantidad * Math.min(1.6, Math.max(0.45, (ancho * alto) / (1440 * 900))),
  );
  return Math.min(cuantas, conf.maximo ?? Infinity);
}

/* Arranca el ambiente de `tema` sobre `canvas`. Devuelve cómo pararlo, o
 * `null` si ese tema no tiene ambiente o no hay con qué dibujar (y entonces
 * quien llama se queda con el fondo quieto). */
export function crearAmbiente(canvas, tema, { sobreClaro = true } = {}) {
  const conf = AMBIENTES[tema];
  if (!conf) return null;
  let g = null;
  try {
    g = canvas.getContext("2d");
  } catch {
    g = null;
  }
  if (!g) return null;

  const { tipo } = conf;
  // `dibujadas` son las de código; `estampas` arranca siendo ésas y pasa a
  // ser las fotos cuando la plancha termina de llegar.
  const conSellos = tipo === "copo" || tipo === "petalo" || tipo === "hoja";
  const dibujadas = conSellos ? sellos(tipo, sobreClaro) : null;
  let estampas = dibujadas;
  let vivo = true;
  // Los billetes de Batman, entre la lluvia: no se dibujan hasta que llega
  // su plancha (no hay billete dibujado por código).
  let billetes = [];
  let estampasBilletes = null;
  if (conf.billetes && typeof Image !== "undefined") {
    const img = new Image();
    img.onload = () => {
      if (!vivo) return;
      try {
        estampasBilletes = sellosDeFoto(img);
      } catch {
        // Sin billetes: queda la lluvia sola.
      }
    };
    img.src = planchaBilletes;
  }
  if (PLANCHAS[tipo] && typeof Image !== "undefined") {
    const img = new Image();
    img.onload = () => {
      if (!vivo) return;
      try {
        estampas = sellosDeFoto(img);
      } catch {
        // Se queda con las dibujadas.
      }
    };
    img.src = PLANCHAS[tipo];
  }
  // En una pantalla de mucha densidad no hace falta dibujar al triple: no se
  // nota en algo fuera de foco y cuesta nueve veces más.
  const escala = Math.min(window.devicePixelRatio || 1, 2);
  let ancho = 0;
  let alto = 0;
  // Las partículas, las arañas en Spider-Man, los zarcillos en Venom o las
  // gotas de lluvia en Batman: cada una lleva su estado.
  let particulas = [];
  // Sólo las usa Venom: las gotas que cuelgan de un zarcillo o ya caen, la
  // mancha grande de atrás y las trenzas (las de los bordes y las que le
  // salen a la mancha).
  let gotas = [];
  const mancha = conf.mancha ? nuevaMancha() : null;
  let trenzas = [];
  let trenzasMancha = [];
  const sello = mancha ? selloMancha() : null;
  // Cada cuántos cuadros se rehace el dibujo de la mancha, y cuántos faltan
  // para la próxima (al cambiar el tamaño de la ventana, ninguno). Son tres;
  // si rehacerlo tarda más de medio cuadro —una PC sin placa de video, que
  // pinta todo con el procesador—, se espacia, hasta uno de cada nueve: en
  // vez de perder un cuadro cada tres, pierde uno cada nueve. Medido con la
  // CPU frenada cuatro veces: 44 cuadros por segundo con tres fijo, contra
  // 57 sin la mancha. Si vuelve a tardar poco, se acerca de a uno a tres.
  //
  // EL PISO (revisión del 09/10). Espaciarlo no abarata cada vez que se
  // rehace: a 1920×1080, con la CPU frenada cuatro veces y pintando con el
  // procesador, cada una seguía tardando de 11 a 27 ms aun de a una cada
  // nueve —un cuadro largo cada 150 ms, y los zarcillos andaban a los
  // tirones—. Así que si ya espaciada al tope tarda de más cuatro veces
  // seguidas, esa PC no da abasto y la mancha deja de cambiar de forma
  // (`quietaMancha` es el instante en que se quedó): se sigue estampando el
  // último dibujo, que es lo barato, donde la lleva el paseo. Pasea, pero no
  // se rehace. Los primeros cuatro segundos no cuentan: con la página
  // cargando, el procesador está ocupado en otra cosa y lo que tarde ahí no
  // dice nada de la PC (medido: sin frenar la CPU, en plena carga ya se
  // quedaba quieta, y la prueba de abajo, hecha también en plena carga, la
  // hacía irse). Al cambiar el tamaño de la ventana se rehace una vez (el
  // dibujo es otro) y, si esa vez tarda poco, vuelve a cambiar de forma.
  //
  // Quieta NO es para siempre (segunda revisión del 09/10): también se queda
  // quieta una PC que puede con ella si le toca un pico de unos segundos —otro
  // programa que arranca, un antivirus—: medido en la app construida, con la
  // CPU a velocidad normal y frenada tres veces durante dos segundos, quedaba
  // quieta el resto de la sesión, a 60 cuadros por segundo. Así que, quieta,
  // cada REPRUEBA_MANCHA cuadros (diez segundos a 60) se rehace una vez: si
  // tarda poco, vuelve a cambiar de forma. Y como la forma va con su propio
  // reloj (`relojForma`), que se para mientras está quieta, sigue desde donde
  // quedó: no salta a la de diez segundos después.
  //
  // Y estampar tampoco es gratis: es un quinto de la pantalla para pintar en
  // cada cuadro. Medido así, con la mancha ya quieta: a 1440×900 la página
  // queda como sin la mancha (58 cuadros por segundo contra 59), pero a
  // 1920×1080 seguía en 35 a 49 contra 46 a 55. Así que se miran 120 cuadros
  // (`pruebaQuieta`) y, si van a menos de 52 por segundo (más de uno de cada
  // siete se pierde), la mancha se va: se apaga en un segundo y medio
  // (`seVaMancha` es cuándo empezó) y no vuelve hasta recargar la página. Esa
  // PC queda como estaba antes de la mancha. Como irse es para siempre, no se
  // mide apenas se queda quieta sino recién cuando, diez segundos después, la
  // vuelta a probar también sale lenta: medido en plena prueba, un pico de
  // cuatro segundos (la CPU frenada seis veces) la hacía irse en una PC que
  // antes y después anda a 60. Y se mide una sola vez, aunque después vuelva
  // a cambiar de forma y a quedarse quieta: si no, una tabla pesada que tarda
  // en armarse más tarde la haría desaparecer en cualquier momento.
  const TOPE_MANCHA = 9;
  const REPRUEBA_MANCHA = 600;
  let cadaMancha = 3;
  let faltaMancha = 0;
  let lentasMancha = 0;
  let quietaMancha = false;
  let relojForma = 0;
  const pruebaQuieta = { armada: false, cuadros: 0, tiempo: 0 };
  let seVaMancha = null;
  let manchaIda = false;
  // EL PISO DE LAS TRENZAS (13/10). Son lo que más cuesta pintar del ambiente:
  // medido en Chromium pintando con el procesador, una trenza cuesta como dos
  // zarcillos, y con las cinco a 1440×900 y la CPU frenada dos veces el
  // ambiente andaba a 54 cuadros por segundo (sin trenzas, 59). Así que se
  // cuentan los cuadros, de a 60 y pasados los primeros cuatro segundos (la
  // carga), y se mira cuántos llegaron tarde.
  //
  // Tarde es lo que tardó bastante más de lo que da esa pantalla: más de 1,4
  // veces su cuadro más corto de las últimas tandas, y nunca menos de 25 ms.
  // Un celular con ahorro de batería da 30 por segundo aunque le sobre
  // procesador: contra 25 ms fijos todos sus cuadros llegaban tarde y las
  // trenzas se iban para siempre (la mancha no: su piso mide lo que tarda su
  // propio dibujo).
  //
  // Si en una tanda llegó tarde más de uno de cada quince (la mancha aguanta
  // hasta 52 por segundo, pero las trenzas son lo que más cuesta y lo primero
  // que sobra), sobran según cuántos: con pocos, una; con uno de cada cuatro,
  // la mitad; con la mitad o más, todas. Se apagan en un segundo y medio
  // (primero las de los bordes; una escondida se va enseguida). Antes sobraba
  // una por tanda de 120 cuadros, y no alcanzaba (revisión del 13/10): a
  // 1920×1080 con la CPU frenada dos veces, la página iba a 34-47 cuadros por
  // segundo los primeros once segundos, y de siete a ninguna tardaba unos
  // cuarenta.
  //
  // Vuelven de a una, que asoma de a poco, después de veinte segundos buenos:
  // un pico —una tabla pesada que se arma, el antivirus— no las deja afuera
  // para siempre. Pero si una que volvió no se banca, la próxima espera el
  // doble (hasta 160 s): si no, en una PC justo en el límite el cupo iba y
  // venía. Y si la mancha se fue, el cupo queda en cero: esa PC se queda sin
  // trenzas y sin mancha.
  //
  // Medido (revisión del 13/10) en una página con el ambiente solo, Chromium
  // pintando con el procesador y la CPU frenada, segundo a segundo el primer
  // minuto. A 1920×1080 frenada dos veces arrancan las seis (cuatro de los
  // bordes y dos de la mancha) y la página anda a 21-57 cuadros por segundo;
  // a los 8 o 9 s quedan entre ninguna y tres y anda a 52-61, como sin
  // trenzas. Antes eran siete a 33-49 los primeros diez segundos, y ninguna
  // recién a los 40 s. Frenada tres o cuatro veces, a los 9-10 s ya no
  // queda ninguna (antes, a los 28 y a los 35 s). Los primeros ocho segundos
  // siguen lentos en esas PC: cuatro de carga que no se cuentan, una tanda y
  // lo que tardan en apagarse. Y frenada cuatro veces a 1920×1080, ya sin
  // trenzas ni mancha, anda a 35-41 contra 45-50 de antes de las trenzas: son
  // los zarcillos y las gotas, que ahí son diez y cinco (antes, ocho y
  // cuatro); con los de antes, 43-49.
  const piso = nuevoPisoTrenzas();
  let fugaz = null;
  // Sólo la usa Arcade: los invasores que ya cayeron y el disparo en vuelo.
  const partida = nuevaPartida();
  // Las arañas de Spider-Man son de foto: la plancha se cuelga de la partida
  // cuando termina de llegar. Mientras tanto, la de código.
  if (tipo === "arana" && typeof Image !== "undefined") {
    const img = new Image();
    img.onload = () => {
      if (vivo) partida.aranas = img;
    };
    img.src = planchaAranas;
  }
  let proximaFugaz = 6 + Math.random() * 8;

  // Las trenzas que están y no se están apagando.
  const vivas = () => {
    let n = 0;
    for (const tr of trenzas) if (tr.seVa == null) n++;
    for (const tr of trenzasMancha) if (tr.seVa == null) n++;
    return n;
  };
  // Suma las que faltan hasta las que pide la pantalla, sin pasar del cupo;
  // primero las de la mancha. `entra`: escondidas, para que asomen de a poco.
  const completarTrenzas = (entra) => {
    const bordes = conf.trenzas ? cuantasVan({ cantidad: conf.trenzas, maximo: conf.maxTrenzas }, ancho, alto) : 0;
    const deLaMancha =
      mancha && conf.trenzasMancha && !manchaIda
        ? cuantasVan({ cantidad: conf.trenzasMancha, maximo: conf.trenzasMancha }, ancho, alto)
        : 0;
    while (trenzasMancha.length < deLaMancha && vivas() < piso.cupo)
      trenzasMancha.push(nuevaTrenzaDeLaMancha(ancho, alto, Math.random, entra, trenzasMancha));
    while (trenzas.length < bordes && vivas() < piso.cupo) trenzas.push(nuevaTrenza(ancho, alto, Math.random, entra));
    if (trenzasMancha.length > deLaMancha) trenzasMancha.length = deLaMancha;
    if (trenzas.length > bordes) trenzas.length = bordes;
  };
  // Cuánto se ve una que se está apagando.
  const apagada = (tr, t) => (tr.seVa == null ? 1 : 1 - (t - tr.seVa) / 1.5);
  // El piso (ver arriba), una vez por cuadro: la cuenta, las que sobran se
  // apagan y, si hay lugar, vuelve una.
  const pisoTrenzas = (t, intervalo) => {
    if (manchaIda) piso.cupo = 0;
    else contarCuadroTrenzas(piso, t, intervalo, vivas());
    let sobran = vivas() - piso.cupo;
    for (const lista of [trenzas, trenzasMancha])
      for (let i = lista.length - 1; i >= 0 && sobran > 0; i--) {
        if (lista[i].seVa != null) continue;
        if (lista[i].fase === "oculto") lista.splice(i, 1);
        else lista[i].seVa = t;
        sobran--;
      }
    if (piso.cupo !== Infinity && vivas() < piso.cupo) completarTrenzas(true);
  };

  const medir = () => {
    ancho = canvas.clientWidth || window.innerWidth;
    alto = canvas.clientHeight || window.innerHeight;
    canvas.width = Math.round(ancho * escala);
    canvas.height = Math.round(alto * escala);
    faltaMancha = 0;
    // Una pantalla grande lleva más; un celular, menos.
    const cuantas = cuantasVan(conf, ancho, alto);
    while (particulas.length < cuantas)
      particulas.push(
        tipo === "arana"
          ? nuevaArana(ancho, alto)
          : tipo === "simbionte"
            ? nuevoZarcillo(ancho, alto)
            : tipo === "lluvia"
              ? nuevaGotaLluvia(ancho, alto)
              : nuevaParticula(tipo, ancho, alto),
      );
    particulas.length = cuantas;
    if (conf.billetes) {
      const cuantosBilletes = cuantasVan(conf.billetes, ancho, alto);
      while (billetes.length < cuantosBilletes) billetes.push(nuevaParticula("billete", ancho, alto));
      billetes.length = cuantosBilletes;
      billetes.sort((a, b) => a.z - b.z);
    }
    // Si al achicar la ventana se fue el zarcillo del que colgaba una gota,
    // la gota se suelta: si no, se queda colgando de la nada (de un ancla que
    // ya no se mueve) hasta terminar de formarse, y ocupando un lugar.
    for (const gt of gotas)
      if (gt.fase === "cuelga" && !particulas.includes(gt.zarcillo)) {
        gt.fase = "cae";
        gt.vy = 8;
      }
    // Las trenzas, lo mismo: más en una pantalla grande (sin pasar del cupo).
    completarTrenzas(false);
    if (conSellos) particulas.sort((a, b) => a.z - b.z);
  };

  const dibujarEstrellas = (t, dt) => {
    for (const p of particulas) {
      const titilar = 0.55 + 0.45 * Math.sin(t * (0.6 + p.ritmo * 1.8) + p.fase);
      g.globalAlpha = p.alfa * titilar;
      g.fillStyle = COLORES.estrella[p.sello % COLORES.estrella.length];
      g.beginPath();
      g.arc(p.x, p.y, p.tam, 0, Math.PI * 2);
      g.fill();
      // Las más cercanas tienen un halo.
      if (p.z > 0.8) {
        g.globalAlpha = p.alfa * titilar * 0.16;
        g.beginPath();
        g.arc(p.x, p.y, p.tam * 4, 0, Math.PI * 2);
        g.fill();
      }
    }
    // De tanto en tanto, una fugaz. Rara: si pasa seguido deja de ser linda.
    proximaFugaz -= dt;
    if (!fugaz && proximaFugaz <= 0) {
      fugaz = {
        x: Math.random() * ancho * 0.7,
        y: Math.random() * alto * 0.4,
        vida: 0,
      };
      proximaFugaz = 9 + Math.random() * 14;
    }
    if (fugaz) {
      fugaz.vida += dt;
      const k = fugaz.vida / 0.9;
      if (k >= 1) fugaz = null;
      else {
        const x = fugaz.x + k * 320;
        const y = fugaz.y + k * 130;
        const cola = g.createLinearGradient(x, y, x - 120, y - 49);
        cola.addColorStop(0, `rgba(255,255,255,${0.9 * (1 - k)})`);
        cola.addColorStop(1, "rgba(255,255,255,0)");
        g.globalAlpha = 1;
        g.strokeStyle = cola;
        g.lineWidth = 1.4;
        g.beginPath();
        g.moveTo(x, y);
        g.lineTo(x - 120, y - 49);
        g.stroke();
      }
    }
  };

  // `intervalo` es lo que pasó de verdad desde el cuadro anterior, sin
  // recortar (`null` en el primero): lo usa el piso de las trenzas.
  // Una partícula con su sello: las de bien adelante salen fuera de foco.
  const sellar = (p, origen) => {
    const juego = p.z > 0.72 ? origen.borrosos : origen.nitidos;
    const sello = juego[p.sello % juego.length];
    g.globalAlpha = p.alfa * (p.z > 0.72 ? 0.8 : 1);
    g.setTransform(escala, 0, 0, escala, p.x * escala, p.y * escala);
    if (p.vgiro) g.rotate(p.giro);
    // Darse vuelta: el ancho se achica hasta el canto y vuelve.
    if (p.vvuelta) g.scale(Math.cos(p.vuelta) * 0.92 + 0.08 * Math.sign(Math.cos(p.vuelta) || 1), 1);
    g.drawImage(sello, -p.tam, -p.tam, p.tam * 2, p.tam * 2);
  };

  const dibujar = (t, dt, intervalo = null) => {
    g.setTransform(escala, 0, 0, escala, 0, 0);
    g.clearRect(0, 0, ancho, alto);
    if (tipo === "estrella") {
      dibujarEstrellas(t, dt);
      return;
    }
    if (tipo === "radar") {
      dibujarRadar(g, t, ancho, alto);
      return;
    }
    if (tipo === "rejilla") {
      dibujarRejilla(g, t, ancho, alto);
      return;
    }
    const escena = ESCENAS[tipo];
    if (escena) {
      escena(g, t, ancho, alto, partida);
      return;
    }
    if (tipo === "lluvia") {
      for (let i = 0; i < particulas.length; i++)
        if (moverLluvia(particulas[i], dt, ancho, alto))
          particulas[i] = nuevaGotaLluvia(ancho, alto, Math.random, true);
      dibujarLluvia(g, particulas, sobreClaro);
      // Los billetes van delante de la lluvia: son más grandes y más cerca.
      if (estampasBilletes) {
        for (let i = 0; i < billetes.length; i++) {
          if (avanzar(billetes[i], dt, ancho, alto, t)) billetes[i] = nuevaParticula("billete", ancho, alto, Math.random, true);
          else sellar(billetes[i], estampasBilletes);
        }
        g.setTransform(escala, 0, 0, escala, 0, 0);
        g.globalAlpha = 1;
      }
      return;
    }
    if (tipo === "arana") {
      const tinta = sobreClaro ? conf.colores : COLORES.arana;
      for (let i = 0; i < particulas.length; i++) {
        if (moverArana(particulas[i], dt, ancho, alto, t))
          particulas[i] = nuevaArana(ancho, alto, Math.random, true);
        const a = particulas[i];
        dibujarArana(g, a, escala, tinta[a.color % tinta.length], partida.aranas);
      }
      return;
    }
    if (tipo === "simbionte") {
      if (conf.trenzas || conf.trenzasMancha) pisoTrenzas(t, intervalo);
      // La mancha va primero: es lo de más atrás, y un zarcillo que le pasa
      // por encima se ve como goo que se junta con goo. Su dibujo se rehace
      // cada tantos cuadros (ver `estamparMancha` y `cadaMancha`). Lo único
      // que va antes son sus trenzas: le salen de abajo, así que la raíz la
      // tapa ella misma y no queda una costura donde se juntan.
      if (mancha && !manchaIda) {
        // Quieta, el reloj de la forma no anda (la forma es la del instante en
        // que se quedó); el paseo sigue con el de la pantalla.
        if (!quietaMancha) relojForma += dt;
        formaMancha(mancha, t, ancho, alto, relojForma);
        const alfa = seVaMancha === null ? 1 : 1 - (t - seVaMancha) / 1.5;
        // Quieta y sin rehacerse, se estampa el último dibujo con el centro
        // corrido lo que haga falta (ver `acomodarSello`): se corre antes de
        // las trenzas, que se agarran de ese centro.
        if (alfa > 0 && sello && faltaMancha > 0 && quietaMancha) acomodarSello(mancha, sello, ancho, alto);
        // Las trenzas de la mancha van y vienen con el paseo y se apagan con
        // ella si se va. Una que se metió sale de nuevo por otro lado.
        if (alfa > 0)
          for (let i = 0; i < trenzasMancha.length; i++) {
            const tr = trenzasMancha[i];
            const termino = moverTrenza(tr, dt, ancho, alto, t, mancha);
            if (apagada(tr, t) <= 0 || (termino && tr.seVa != null)) {
              trenzasMancha.splice(i--, 1);
              continue;
            }
            if (termino)
              trenzasMancha[i] = nuevaTrenzaDeLaMancha(
                ancho, alto, Math.random, true, trenzasMancha.filter((_, j) => j !== i),
              );
            dibujarTrenza(g, trenzasMancha[i], TINTA_SIMBIONTE, alfa * apagada(trenzasMancha[i], t));
          }
        if (alfa <= 0) {
          // Se fue: el lienzo aparte se achica para soltar la memoria, y sus
          // trenzas se van con ella.
          manchaIda = true;
          sello.lienzo.width = 1;
          sello.lienzo.height = 1;
          trenzasMancha = [];
        } else if (!sello) dibujarMancha(g, mancha);
        else if (faltaMancha > 0) {
          faltaMancha--;
          if (quietaMancha) {
            if (seVaMancha === null && pruebaQuieta.armada && pruebaQuieta.cuadros < 120) {
              pruebaQuieta.cuadros++;
              pruebaQuieta.tiempo += dt;
              if (pruebaQuieta.cuadros === 120 && pruebaQuieta.tiempo / 120 > 1 / 52) seVaMancha = t;
            }
          }
          estamparMancha(g, mancha, sello, escala, false, alfa);
        } else {
          const desde = performance.now();
          estamparMancha(g, mancha, sello, escala, true, alfa);
          const lenta = performance.now() - desde > 8;
          if (!lenta) lentasMancha = 0;
          else if (cadaMancha === TOPE_MANCHA && t > 4) lentasMancha++;
          if (!lenta) {
            quietaMancha = false;
            pruebaQuieta.armada = false;
            // Una prueba a medias (cambió el tamaño en el medio) no vale; una
            // entera ya se hizo y no se repite (ver arriba).
            if (pruebaQuieta.cuadros < 120) {
              pruebaQuieta.cuadros = 0;
              pruebaQuieta.tiempo = 0;
            }
          } else if (quietaMancha) pruebaQuieta.armada = true;
          else if (lentasMancha >= 4) quietaMancha = true;
          cadaMancha = lenta ? Math.min(TOPE_MANCHA, cadaMancha * 2) : Math.max(3, cadaMancha - 1);
          // Quieta se vuelve a probar cada tanto, o enseguida al cambiar el
          // tamaño (`medir`).
          faltaMancha = quietaMancha ? REPRUEBA_MANCHA : cadaMancha - 1;
        }
      }
      // Las trenzas de los bordes. Una que se metió sale de nuevo por otro
      // lado; una que se estaba apagando (el piso), no.
      for (let i = 0; i < trenzas.length; i++) {
        const tr = trenzas[i];
        const termino = moverTrenza(tr, dt, ancho, alto, t);
        if (apagada(tr, t) <= 0 || (termino && tr.seVa != null)) {
          trenzas.splice(i--, 1);
          continue;
        }
        if (termino) trenzas[i] = nuevaTrenza(ancho, alto, Math.random, true);
        dibujarTrenza(g, trenzas[i], TINTA_SIMBIONTE, apagada(trenzas[i], t));
      }
      // Primero se mueven los zarcillos (las gotas colgadas van con ellos),
      // después las gotas. Las gotas se dibujan antes: el cuello entra por
      // abajo del zarcillo del que cuelga.
      for (let i = 0; i < particulas.length; i++) {
        if (moverZarcillo(particulas[i], dt, ancho, alto, t))
          particulas[i] = nuevoZarcillo(ancho, alto, Math.random, true);
        const zr = particulas[i];
        if (zr.gotea && gotas.length < (conf.gotas || 0)) gotas.push(nuevaGota(zr));
      }
      gotas = gotas.filter((gt) => !moverGota(gt, dt, alto));
      for (let i = 0; i < gotas.length; i++) dibujarGota(g, gotas[i]);
      for (let i = 0; i < particulas.length; i++) dibujarZarcillo(g, particulas[i]);
      return;
    }
    if (tipo === "papelito") {
      for (let i = 0; i < particulas.length; i++) {
        const p = particulas[i];
        if (avanzar(p, dt, ancho, alto, t)) {
          particulas[i] = nuevaParticula(tipo, ancho, alto, Math.random, true);
          continue;
        }
        // Un rectangulito que gira y se da vuelta: de canto casi desaparece,
        // que es lo que lo hace leerse como papel.
        g.globalAlpha = p.alfa;
        g.fillStyle = conf.colores[p.sello % conf.colores.length];
        g.setTransform(escala, 0, 0, escala, p.x * escala, p.y * escala);
        g.rotate(p.giro);
        g.scale(1, Math.cos(p.vuelta) * 0.92 + 0.08);
        g.fillRect(-p.tam, -p.tam * 0.62, p.tam * 2, p.tam * 1.24);
        // Sobre un fondo claro el papelito blanco necesita un filo.
        if (sobreClaro) {
          g.strokeStyle = "rgba(17,17,17,0.18)";
          g.lineWidth = 0.6;
          g.strokeRect(-p.tam, -p.tam * 0.62, p.tam * 2, p.tam * 1.24);
        }
      }
      g.setTransform(escala, 0, 0, escala, 0, 0);
      g.globalAlpha = 1;
      return;
    }
    if (tipo === "pixel") {
      for (let i = 0; i < particulas.length; i++) {
        const p = particulas[i];
        if (avanzar(p, dt, ancho, alto, t)) {
          particulas[i] = nuevaParticula(tipo, ancho, alto, Math.random, true);
          continue;
        }
        // Cuadrados clavados a la grilla de píxeles: si quedan a medio píxel
        // se ven borrosos, y lo borroso acá es lo contrario del tema.
        const lado = Math.max(1, Math.round(p.tam)) * 2;
        const parpadeo = p.z > 0.6 ? 0.7 + 0.3 * Math.sin(t * 5 * p.ritmo + p.fase) : 1;
        g.globalAlpha = p.alfa * parpadeo;
        g.fillStyle = COLORES.pixel[p.sello % COLORES.pixel.length];
        g.fillRect(Math.round(p.x), Math.round(p.y), lado, lado);
      }
      g.globalAlpha = 1;
      return;
    }
    for (let i = 0; i < particulas.length; i++) {
      const p = particulas[i];
      if (avanzar(p, dt, ancho, alto, t)) {
        particulas[i] = nuevaParticula(tipo, ancho, alto, Math.random, true);
        continue;
      }
      // El copo LEJANO es un punto blando, no un cristal: a seis píxeles el
      // dibujo de la foto es ruido, y en una nevada lo de atrás se ve así.
      sellar(p, tipo === "copo" && p.z < 0.42 ? dibujadas : estampas);
    }
    g.globalAlpha = 1;
  };

  let cuadro = 0;
  let antes = 0;
  let reloj = 0;
  const paso = (ahora) => {
    // Al volver de otra pestaña el salto de tiempo sería de minutos y caería
    // todo de golpe: se lo recorta.
    const intervalo = antes ? (ahora - antes) / 1000 : null;
    const dt = Math.min(0.05, intervalo ?? 0.016);
    antes = ahora;
    reloj += dt;
    dibujar(reloj, dt, intervalo);
    cuadro = window.requestAnimationFrame(paso);
  };

  const alVolver = () => {
    window.cancelAnimationFrame(cuadro);
    antes = 0;
    // Con la pestaña escondida no se dibuja nada.
    if (!document.hidden) cuadro = window.requestAnimationFrame(paso);
  };

  medir();
  window.addEventListener("resize", medir);
  document.addEventListener("visibilitychange", alVolver);
  cuadro = window.requestAnimationFrame(paso);

  return () => {
    vivo = false;
    window.cancelAnimationFrame(cuadro);
    window.removeEventListener("resize", medir);
    document.removeEventListener("visibilitychange", alVolver);
  };
}
